import React, { useState, lazy, Suspense } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'motion/react';
import { 
  Shield, 
  ChevronRight, 
  Globe, 
  Lock, 
  Zap, 
  Gift, 
  X, 
  Play, 
  Menu,
  Monitor,
  Laptop,
  Smartphone,
  ArrowRight,
  Info,
  Layers,
  Search,
  MessageSquare,
  Users,
  Volume2,
  VolumeX,
  CheckCircle,
  LayoutGrid,
  Mic,
  WifiOff,
  Download,
  Mail,
  Send,
  ExternalLink,
  PictureInPicture2
} from 'lucide-react';
import { cn, safeRequestFullscreen } from '../lib/utils';
import { soundManager } from '../lib/sound';
import { usePiP } from '../lib/PiPContext';
import { PiPActivePlaceholder } from './PiPActivePlaceholder';
const Lobby = lazy(() => import('./Lobby').then(m => ({ default: m.Lobby || m.default })));
const SidebarMenu = lazy(() => import('./SidebarMenu').then(m => ({ default: m.SidebarMenu || m.default })));

import { SocialShare } from './SocialShare';
import { HeroShowcaseInteractive } from './HeroShowcaseInteractive';
import { IskambilBacklinkBanner } from './IskambilBacklinkBanner';
import { useTranslation } from '../lib/translations';
import { GameState } from '../types';

interface LandingPageProps {
  myId: string;
  shortId?: string;
  onJoin: (peerId: string, name: string, avatar?: string) => void;
  onHost: (name: string, type?: any, mode?: 'AI' | 'P2P', isTeamGame?: boolean, batakVariant?: 'ihaleli' | 'kozmaca' | 'gomulu' | 'esli', avatar?: string, maxPlayers?: number, targetScore?: number, bluffingMode?: boolean) => void;
  playersCount: number;
  onGameTypeChange: (type: any) => void;
  gameType: string;
  selectedSkinId: number;
  onSkinSelect: (id: number) => void;
  faceStyle: GameState['faceStyle'];
  onFaceStyleSelect: (style: any) => void;
  userAvatar: string;
  onAvatarChange: (avatar: string) => void;
  userName: string;
  onNameChange: (name: string) => void;
  stats: { gamesPlayed: number; gamesWon: number; totalScore: number };
  colorTheme: string;
  onColorThemeSelect: (color: string) => void;
  onLogout: () => void;
  onOpenCookiePolicy: () => void;
  onOpenTermsOfUse: () => void;
  isConnecting?: boolean;
  initialShowLobby?: boolean;
  initialLobbyStep?: 'TYPE' | 'MODE' | 'JOIN';
  initialRoomCode?: string;
  onLobbyClose?: () => void;
}

export const LandingPage: React.FC<LandingPageProps> = (props) => {
  const { t, language, setLanguage } = useTranslation();
  const { isSupported: isPiPSupported, isPiPActive, pipContainer, togglePiP, closePiP } = usePiP();
  const [showLobbyModal, setShowLobbyModal] = useState(props.initialShowLobby || !!props.initialRoomCode || false);
  const [lobbyInitialStep, setLobbyInitialStep] = useState<'TYPE' | 'MODE' | 'JOIN'>(props.initialLobbyStep ?? (props.initialRoomCode ? 'JOIN' : 'TYPE'));

  React.useEffect(() => {
    if (props.initialShowLobby !== undefined) {
      setShowLobbyModal(props.initialShowLobby);
    } else if (props.initialRoomCode) {
      setShowLobbyModal(true);
    }
  }, [props.initialShowLobby, props.initialRoomCode]);

  React.useEffect(() => {
    if (props.initialLobbyStep) {
      setLobbyInitialStep(props.initialLobbyStep);
    }
  }, [props.initialLobbyStep]);

  const [showHowToPlayModal, setShowHowToPlayModal] = useState(false);
  const [showAboutModal, setShowAboutModal] = useState(false);
  const [showP2PModal, setShowP2PModal] = useState(false);
  const [showContactModal, setShowContactModal] = useState(false);
  const [contactEmail, setContactEmail] = useState('');
  const [contactType, setContactType] = useState('Öneri');
  const [contactMessage, setContactMessage] = useState('');
  const [contactStatus, setContactStatus] = useState<'idle' | 'sending' | 'success'>('idle');
  const [contactCopied, setContactCopied] = useState(false);

  const copyEmailToClipboard = async () => {
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText('mail@pisti.online');
      } else {
        const textArea = document.createElement('textarea');
        textArea.value = 'mail@pisti.online';
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
      }
      setContactCopied(true);
      soundManager.play('click');
      setTimeout(() => setContactCopied(false), 2000);
    } catch (e) {
      console.warn('Copy failed:', e);
    }
  };
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isMuted, setIsMuted] = useState(soundManager.isMuted());

  React.useEffect(() => {
    const handleSoundToggle = () => setIsMuted(soundManager.isMuted());
    window.addEventListener('soundToggle', handleSoundToggle);
    return () => window.removeEventListener('soundToggle', handleSoundToggle);
  }, []);

  const openLobbyWithGame = (type: string) => {
    safeRequestFullscreen();
    let t = 'pisti';
    const upperType = type.toUpperCase();
    if (upperType.includes('BATAK')) t = 'batak';
    if (upperType.includes('KING') || upperType.includes('KİNG')) t = 'king';
    if (upperType.includes('OKEY')) t = 'okey';
    props.onGameTypeChange(t);
    setLobbyInitialStep('MODE');
    setShowLobbyModal(true);
  };

  const toggleModal = () => {
    if (!showLobbyModal) {
      safeRequestFullscreen();
      setLobbyInitialStep('TYPE');
    }
    const nextState = !showLobbyModal;
    setShowLobbyModal(nextState);
    if (!nextState && props.onLobbyClose) {
      props.onLobbyClose();
    }
  };

  const renderedContent = (
    <div id="landing-page-root" className="min-h-screen bg-[#020617] text-white flex flex-col font-sans selection:bg-cyan-500/30">
      {/* Header */}
      <header id="main-header" className="fixed top-0 left-0 w-full z-[100] bg-[#020617]/80 backdrop-blur-md border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 h-20 flex items-center justify-between">
          <div id="logo-container" className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-cyan-500/10 border border-cyan-500/30 flex items-center justify-center shadow-[0_0_15px_rgba(34,211,238,0.2)]">
              <Shield className="w-6 h-6 text-cyan-400" />
            </div>
            <div>
              <h1 className="text-xl font-black tracking-tighter leading-none">Pisti.online</h1>
              <p className="text-[10px] text-cyan-400/90 font-medium tracking-widest uppercase">The Card Game +</p>
            </div>
          </div>

          <nav id="top-nav" className="hidden lg:flex items-center gap-10">
            <a 
              href="#" 
              onClick={(e) => { e.preventDefault(); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
              className="text-[11px] font-black tracking-widest transition-colors text-cyan-400 border-b-2 border-cyan-400 pb-1"
            >
              {language === 'tr' ? 'ANA SAYFA' : 'HOME'}
            </a>
            <a 
              href="#" 
              onClick={(e) => { e.preventDefault(); const gamesSection = document.getElementById('oyunlar-section'); if(gamesSection) gamesSection.scrollIntoView({ behavior: 'smooth' }); }}
              className="text-[11px] font-black tracking-widest transition-colors text-white/75 hover:text-white"
            >
              {language === 'tr' ? 'OYUNLAR' : 'GAMES'}
            </a>
            <a 
              href="#" 
              onClick={(e) => { e.preventDefault(); openLobbyWithGame('okey'); }}
              className="text-[11px] font-black tracking-widest transition-colors text-amber-400/90 hover:text-amber-300 flex items-center gap-1"
            >
              OKEY
            </a>
            <a 
              href="#" 
              onClick={(e) => { e.preventDefault(); setShowHowToPlayModal(true); }}
              className="text-[11px] font-black tracking-widest transition-colors text-white/75 hover:text-white"
            >
              {language === 'tr' ? 'NASIL OYNANIR' : 'HOW TO PLAY'}
            </a>
            <button
              onClick={() => setShowContactModal(true)}
              className="text-[11px] font-black tracking-widest transition-colors text-white/75 hover:text-white uppercase"
            >
              {language === 'tr' ? 'İLETİŞİM' : 'CONTACT'}
            </button>
            <a 
              href="#" 
              onClick={(e) => { e.preventDefault(); setShowP2PModal(true); }}
              className="text-[11px] font-black tracking-widest transition-colors text-white/75 hover:text-white"
            >
              {t('architecture')}
            </a>
          </nav>

          <div className="flex items-center gap-4">
            <button 
              id="header-play-button"
              onClick={toggleModal}
              className="hidden sm:flex group relative items-center justify-center gap-2 px-6 py-2.5 bg-blue-700 bg-gradient-to-br from-cyan-500 to-blue-700 rounded-xl font-black text-xs tracking-widest text-white overflow-hidden shadow-[0_0_20px_-5px_rgba(6,182,212,0.4)] hover:shadow-[0_0_30px_-5px_rgba(6,182,212,0.6)] transition-all active:scale-95 border border-cyan-400/50 hover:border-cyan-300"
            >
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-[150%] skew-x-[-30deg] group-hover:translate-x-[150%] transition-transform duration-1000 ease-in-out" />
              <div className="relative z-10 flex items-center justify-center w-5 h-5 bg-white/20 rounded-full group-hover:bg-white group-hover:text-blue-600 transition-colors shadow-inner">
                <Play className="w-2.5 h-2.5 ml-0.5 fill-current" />
              </div>
              <span className="relative z-10 drop-shadow-md">{t('play_now')}</span>
            </button>
            <div className="flex items-center gap-1">
              {isPiPSupported && (
                <button
                  onClick={() => {
                    togglePiP();
                    soundManager.play('click');
                  }}
                  title={
                    isPiPActive
                      ? (language === 'tr' ? 'Ana Ekrana Dön' : 'Restore Window')
                      : (language === 'tr' ? 'Mini Masa (Picture-in-Picture)' : 'Mini Window (Picture-in-Picture)')
                  }
                  aria-label={
                    isPiPActive
                      ? (language === 'tr' ? 'Ana Ekrana Dön' : 'Restore Window')
                      : (language === 'tr' ? 'Mini Masa (Picture-in-Picture)' : 'Mini Window (Picture-in-Picture)')
                  }
                  className={cn(
                    "p-2 rounded-xl transition-all relative z-50 pointer-events-auto flex items-center justify-center mr-1",
                    isPiPActive
                      ? "text-cyan-300 bg-cyan-500/20 border border-cyan-400/50 shadow-[0_0_12px_rgba(6,182,212,0.4)]"
                      : "text-white/60 hover:text-white hover:bg-white/5"
                  )}
                >
                  <PictureInPicture2 className={cn("w-5 h-5", isPiPActive ? "text-cyan-400" : "")} />
                </button>
              )}
              <button 
                onClick={() => {
                  setLanguage(language === 'tr' ? 'en' : 'tr');
                  soundManager.play('click');
                }}
                title={language === 'tr' ? 'Switch to English' : "Türkçe'ye Geç"}
                aria-label={language === 'tr' ? 'Dili İngilizce Yap' : 'Switch to Turkish'}
                className="p-2 text-white/60 hover:text-white transition-colors relative z-50 pointer-events-auto flex items-center gap-1 mr-1"
              >
                <Globe className="w-5 h-5 text-cyan-400" />
                <span className="text-xs font-black tracking-wider uppercase">{language}</span>
              </button>
              <button 
                onClick={() => {
                  const newState = !isMuted; 
                  soundManager.setMuted(newState); 
                  soundManager.play('click'); 
                  window.dispatchEvent(new Event('soundToggle')); 
                }}
                aria-label={isMuted ? (language === 'tr' ? 'Sesi Aç' : 'Unmute') : (language === 'tr' ? 'Sesi Kapat' : 'Mute')}
                className="p-2 text-white/60 hover:text-white transition-colors relative z-50 pointer-events-auto"
              >
                {isMuted ? <VolumeX className="w-6 h-6" /> : <Volume2 className="w-6 h-6" />}
              </button>
              <button 
                id="mobile-menu-trigger"
                aria-label={language === 'tr' ? 'Ana Menüyü Aç' : 'Open Main Menu'}
                className="p-2 text-white/60 hover:text-white transition-colors relative z-50 pointer-events-auto"
                onClick={() => setIsMenuOpen(true)}
              >
                <Menu className="w-6 h-6" />
              </button>
            </div>
          </div>
        </div>
      </header>

      <main id="main-content" className="flex-grow pt-20">
        {/* Hero Section */}
        <section id="hero-section" className="relative min-h-[90vh] flex items-center overflow-hidden">
          {/* Background Elements */}
          <div className="absolute inset-0 z-0">
            <div className="absolute top-1/4 left-1/4 w-[500px] h-[500px] bg-cyan-500/10 rounded-full blur-[120px]" />
            <div className="absolute bottom-1/4 right-1/4 w-[400px] h-[400px] bg-indigo-500/10 rounded-full blur-[100px]" />
            <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/grid-me.png')] opacity-[0.03]" />
          </div>

          <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-16 items-center relative z-10 w-full">
            <motion.div 
              id="hero-text-content"
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              className="space-y-10"
            >
              <div className="space-y-2">
                <h2 className="text-5xl md:text-7xl lg:text-8xl font-black tracking-tighter leading-tight md:leading-[0.95] text-white uppercase italic pb-2 drop-shadow-sm">
                  {language === 'tr' ? 'PİŞTİ, BATAK, KİNG, OKEY' : 'PISTI, BATAK, KING, OKEY'}
                </h2>
                <h3 className="text-xl md:text-2xl font-black text-cyan-400 tracking-widest uppercase">
                  {language === 'tr' 
                    ? 'DÜŞÜK GECİKMELİ & GÜVENLİ MULTIPLAYER KART & MASA OYUNLARI DENEYİMİ' 
                    : 'LOW LATENCY & SECURE MULTIPLAYER CARD & BOARD GAMES EXPERIENCE'}
                </h3>
              </div>
              
              <p className="text-lg text-white/70 leading-relaxed max-w-lg font-medium">
                {language === 'tr' ? (
                  <>Pisti.online ile klasik kart ve masa oyunlarını modern güvenlik standartlarında arkadaşlarınla oyna. Şifrelenmiş sinyalleme doğrulamalı eşleştirme ve <strong>anti-cheat</strong> korumasıyla adil, izole edilmiş gerçek zamanlı altyapımızla kesintisiz oyunun keyfini çıkar.</>
                ) : (
                  <>Play classic card and board games with your friends on Pisti.online under modern security standards. Enjoy seamless gameplay with our fair, isolated real-time infrastructure featuring encrypted signaling verified matching and <strong>anti-cheat</strong> protection.</>
                )}
              </p>

              <div className="flex flex-col sm:flex-row flex-wrap gap-4 md:gap-5">
                <button 
                  onClick={toggleModal}
                  className="group relative inline-flex items-center justify-center gap-3 px-8 md:px-10 py-4 md:py-5 bg-blue-700 bg-gradient-to-br from-cyan-500 to-blue-700 rounded-2xl font-black text-sm tracking-[0.2em] text-white overflow-hidden shadow-[0_0_40px_-10px_rgba(6,182,212,0.5)] hover:shadow-[0_0_60px_-10px_rgba(6,182,212,0.7)] transition-all active:scale-95 border border-cyan-400/50 hover:border-cyan-300 w-full sm:w-auto"
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-[150%] skew-x-[-30deg] group-hover:translate-x-[150%] transition-transform duration-1000 ease-in-out" />
                  <div className="relative z-10 flex items-center justify-center w-8 md:w-10 h-8 md:h-10 bg-white/20 rounded-full group-hover:bg-white group-hover:text-blue-600 transition-colors shadow-inner">
                    <Play className="w-4 h-4 ml-1 fill-current" />
                  </div>
                  <span className="relative z-10 drop-shadow-md">{t('play_now')}</span>
                </button>
                <button 
                  onClick={() => setShowHowToPlayModal(true)}
                  className="group relative inline-flex items-center justify-center gap-3 px-8 md:px-10 py-4 md:py-5 bg-slate-900/40 backdrop-blur-md rounded-2xl font-bold text-sm tracking-[0.2em] text-white/70 overflow-hidden shadow-lg transition-all active:scale-95 border border-white/10 hover:border-white/30 hover:text-white hover:bg-slate-800/60 w-full sm:w-auto"
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent -translate-x-[150%] skew-x-[-30deg] group-hover:translate-x-[150%] transition-transform duration-1000 ease-in-out" />
                  <span className="relative z-10">{language === 'tr' ? 'NASIL OYNANIR?' : 'HOW TO PLAY?'}</span>
                  <ArrowRight className="relative z-10 w-5 h-5 opacity-50 group-hover:opacity-100 group-hover:translate-x-1.5 transition-all" />
                </button>
                <button 
                  onClick={() => {
                    safeRequestFullscreen();
                    setLobbyInitialStep('JOIN');
                    setShowLobbyModal(true);
                  }}
                  className="group relative inline-flex items-center justify-center gap-3 px-8 md:px-10 py-4 md:py-5 bg-fuchsia-900/40 backdrop-blur-md rounded-2xl font-bold text-sm tracking-[0.2em] text-fuchsia-100 overflow-hidden shadow-lg transition-all active:scale-95 border border-fuchsia-500/30 hover:border-fuchsia-400 hover:bg-fuchsia-800/60 w-full sm:w-auto"
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent -translate-x-[150%] skew-x-[-30deg] group-hover:translate-x-[150%] transition-transform duration-1000 ease-in-out" />
                  <Users className="relative z-10 w-5 h-5 opacity-70 group-hover:opacity-100" />
                  <span className="relative z-10">{language === 'tr' ? 'ARKADAŞINLA OYNA' : 'PLAY WITH FRIEND'}</span>
                </button>
              </div>

              <div className="flex flex-wrap items-center gap-10 pt-10 border-t border-white/5">
                {[
                  { icon: <Zap className="w-6 h-6" />, title: 'HIZLI', desc: '(SIFIR GECİKME)' },
                  { icon: <Monitor className="w-6 h-6" />, title: 'ÖZEL MİMARİ', desc: '(İZOLE TEKNOLOJİ)' },
                  { icon: <Lock className="w-6 h-6" />, title: '%100 GÜVENLİ', desc: 'VERİ SİZDE KALIR' }
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-3 group">
                    <div className="text-cyan-400 group-hover:scale-110 transition-transform">
                      {item.icon}
                    </div>
                    <div className="leading-none">
                      <p className="text-xs font-black tracking-widest">{item.title}</p>
                      <p className="text-[10px] text-white/60 font-bold uppercase">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>

            <motion.div 
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="relative w-full block mt-6 lg:mt-0"
            >
              {/* Interactive 3D Showcase for Mobile & Desktop */}
              <div className="relative z-20 group">
                <HeroShowcaseInteractive onQuickPlay={toggleModal} />
              </div>
            </motion.div>
          </div>
        </section>

        {/* Oyunlarımız Section */}
        <section id="oyunlar-section" className="py-32 bg-black/20 relative">
          <div className="max-w-7xl mx-auto px-6">
            <div className="space-y-4 mb-20 text-center lg:text-left">
              <h2 className="text-4xl md:text-5xl font-black tracking-tighter uppercase italic">{language === 'tr' ? 'OYUNLARIMIZ' : 'OUR GAMES'}</h2>
              <p className="text-white/65 text-lg">
                {language === 'tr' 
                  ? 'Klasik masa ve kart oyunlarını arkadaşlarınla veya zeki botlarla internet üzerinden oyna!' 
                  : 'Play classic board and card games with your friends or smart bots online!'}
              </p>
            </div>

            <div className="grid md:grid-cols-4 gap-8">
              {[
                { 
                  name: 'PİŞTİ', 
                  color: 'from-emerald-900/40 via-emerald-800/20 to-transparent',
                  accent: 'emerald',
                  desc: language === 'tr' ? 'Hızlı & Eğlenceli' : 'Fast & Fun',
                  icon: <Layers className="w-8 h-8 text-emerald-400" />
                },
                { 
                  name: 'BATAK', 
                  color: 'from-amber-900/40 via-amber-800/20 to-transparent',
                  accent: 'amber',
                  desc: language === 'tr' ? 'Taktiksel & Rekabetçi' : 'Tactical & Competitive',
                  icon: <Play className="w-8 h-8 text-amber-400" />
                },
                { 
                  name: 'KİNG', 
                  color: 'from-purple-900/40 via-purple-800/20 to-transparent',
                  accent: 'purple',
                  desc: language === 'tr' ? 'Efsanevi & Karmaşık' : 'Legendary & Complex',
                  icon: <Shield className="w-8 h-8 text-purple-400" />
                },
                {
                  name: 'OKEY',
                  color: 'from-cyan-900/40 via-cyan-800/20 to-transparent',
                  accent: 'cyan',
                  desc: language === 'tr' ? 'Klasik & Stratejik' : 'Classic & Strategic',
                  icon: <Layers className="w-8 h-8 text-cyan-400" />
                }
              ].map((game, i) => (
                <motion.div 
                  key={game.name}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.1 }}
                  viewport={{ once: true }}
                  className={cn(
                    "relative group p-10 rounded-[2.5rem] border border-white/5 overflow-hidden transition-all hover:bg-white/[0.02] bg-gradient-to-br",
                    game.color
                  )}
                >
                  <div className="absolute top-0 right-0 p-8 opacity-20 group-hover:opacity-40 transition-opacity">
                    {game.icon}
                  </div>
                  
                  <div className="relative z-10 space-y-6">
                    <div>
                      <h3 className="text-4xl font-black tracking-tighter italic">{game.name}</h3>
                      <p className="text-white/65 font-bold text-xs uppercase tracking-widest">{game.desc}</p>
                    </div>
                    
                    <div className={cn("flex gap-2", game.name === 'OKEY' ? "opacity-100" : "opacity-60")}>
                      {game.name === 'OKEY' ? (
                        [1, 2, 3, 4].map(idx => (
                          <div key={idx} className="w-10 h-14 bg-gradient-to-b from-[#fdfbf6] via-[#f4ecd8] to-[#e6dbbf] shadow-[0_4px_6px_rgba(0,0,0,0.6),inset_0_-2px_3px_rgba(0,0,0,0.1),inset_0_2px_4px_rgba(255,255,255,1)] rounded-[6px] border-b-[4px] border-b-[#c4b595] border-r-[2px] border-r-[#d1c2a1] border-l-[1px] border-l-[#ffffff] border-t-[1px] border-t-[#ffffff] flex flex-col items-center justify-between pb-1 pt-1.5">
                             <span className={cn("text-[20px] font-black tracking-tighter leading-[0.9] mt-0.5 font-sans", idx === 1 ? 'text-[#ef4444]' : idx === 2 ? 'text-[#1a1a1b]' : idx === 3 ? 'text-[#0ea5e9]' : 'text-[#17852a]')}>
                               7
                             </span>
                             <div className={cn("w-[8px] h-[8px] rounded-full shadow-inner mt-auto mb-1", idx === 1 ? 'bg-[#ef4444]' : idx === 2 ? 'bg-[#1a1a1b]' : idx === 3 ? 'bg-[#0ea5e9]' : 'bg-[#17852a]')} />
                          </div>
                        ))
                      ) : (
                        [1, 2, 3, 4].map(idx => (
                          <div key={idx} className="w-10 h-14 bg-white/10 rounded-lg border border-white/10 flex items-center justify-center">
                             <span className="text-[10px] font-black">{idx === 1 ? 'A' : idx === 2 ? 'K' : idx === 3 ? 'Q' : 'J'}</span>
                          </div>
                        ))
                      )}
                    </div>

                    <button 
                      onClick={() => openLobbyWithGame(game.name)}
                      className={cn(
                        "w-full py-4 rounded-2xl font-black text-xs tracking-[0.3em] uppercase transition-all active:scale-95",
                        game.accent === 'emerald' ? "bg-emerald-700 hover:bg-emerald-600 text-white" :
                        game.accent === 'amber' ? "bg-amber-400 hover:bg-amber-300 text-slate-950" :
                        game.accent === 'cyan' ? "bg-cyan-400 hover:bg-cyan-300 text-slate-950 shadow-[0_0_15px_rgba(6,182,212,0.4)]" :
                        "bg-purple-700 hover:bg-purple-600 text-white"
                      )}
                    >
                      {language === 'tr' ? 'OYNA' : 'PLAY'}
                    </button>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Multiplayer Rehberi Section */}
        <section className="py-24 bg-gradient-to-b from-[#020617] to-cyan-950/20 border-t border-white/5 relative overflow-hidden">
          <div className="max-w-7xl mx-auto px-6 relative z-10">
            <div className="text-center mb-16 space-y-4">
               <h2 className="text-3xl md:text-5xl font-black tracking-tighter uppercase italic text-white drop-shadow-lg">
                 {language === 'tr' ? 'ÇOK OYUNCULU (MULTIPLAYER) NASIL OYNANIR?' : 'HOW TO PLAY MULTIPLAYER?'}
               </h2>
               <p className="text-cyan-400 font-medium tracking-widest uppercase md:text-lg">
                 {language === 'tr' ? 'Sadece 3 basit adımda nerede olurlarsa olsunlar arkadaşlarınızla internet üzerinden oynamaya başlayın.' : 'Start playing over the internet with your friends wherever they are in just 3 simple steps.'}
               </p>
               
               <div className="flex flex-col sm:flex-row gap-4 justify-center items-stretch mt-8 max-w-4xl mx-auto">
                 <div className="flex-1 bg-cyan-500/10 border border-cyan-500/20 rounded-2xl p-4 text-cyan-200/80 font-medium text-sm text-left shadow-lg backdrop-blur-sm">
                   <span className="font-black text-cyan-500 tracking-widest uppercase block mb-1">
                     {language === 'tr' ? '🌐 Kesintisiz Bağlantı' : '🌐 Seamless Connection'}
                   </span>
                   {language === 'tr' ? 'Gelişmiş bulut altyapımız sayesinde arkadaşlarınızla nerede olursanız olun pürüzsüz bir oyun ve mesajlaşma deneyimi yaşayabilirsiniz. Herhangi bir ağ sınırlandırması yoktur.' : 'Thanks to our advanced cloud infrastructure, you can experience smooth gameplay and messaging with your friends wherever you are. There are no network restrictions.'}
                 </div>
                 <div className="flex-1 bg-emerald-500/10 border border-emerald-500/20 rounded-2xl p-4 text-emerald-200/80 font-medium text-sm text-left shadow-lg backdrop-blur-sm flex flex-col justify-between">
                   <div>
                     <span className="font-black text-emerald-400 tracking-widest uppercase mb-1 flex items-center gap-1.5">
                       <Mic className="w-4 h-4 text-emerald-400 animate-pulse" /> 
                       {language === 'tr' ? '🎙️ Canlı Sesli Sohbet (Voice Chat)' : '🎙️ Live Voice Chat'}
                     </span>
                     {language === 'tr' ? 'Oyun esnasında hiçbir ek uygulama (Discord vb.) açmadan, doğrudan masa içinden arkadaşlarınızla gerçek zamanlı sesli konuşabilirsiniz! Mikrofon simgesine tıklayıp konuşmaya başlayın.' : 'Speak with your friends in real-time directly inside the table during the game, without opening any additional apps (Discord, etc.)! Click the microphone icon to start speaking.'}
                   </div>
                 </div>
               </div>
            </div>
            
            <div className="grid md:grid-cols-3 gap-8 mt-12">
               <div className="bg-white/[0.03] border border-white/10 rounded-3xl p-8 hover:bg-white/[0.05] transition-colors relative">
                  <div className="absolute -top-6 left-8 w-12 h-12 bg-cyan-400 text-slate-950 font-black text-2xl flex items-center justify-center rounded-full border-4 border-[#020617] shadow-lg">1</div>
                  <h3 className="text-xl font-black italic uppercase mt-4 mb-3 text-cyan-400">
                    {language === 'tr' ? 'Oda Kur (Host)' : 'Create Room (Host)'}
                  </h3>
                  <p className="text-white/70 leading-relaxed font-medium">
                    {language === 'tr' ? 'Lobiye girerek bir oyun seçin ve "Arkadaşlarla Oyna" (P2P) modunu tıklayın. Oyun ayarlarınızı yapıp odayı kurun.' : 'Enter the lobby, select a game, and click "Play with Friends" (P2P) mode. Adjust your game settings and create the room.'}
                  </p>
               </div>
               
               <div className="bg-white/[0.03] border border-white/10 rounded-3xl p-8 hover:bg-white/[0.05] transition-colors relative">
                  <div className="absolute -top-6 left-8 w-12 h-12 bg-blue-600 text-white font-black text-2xl flex items-center justify-center rounded-full border-4 border-[#020617] shadow-lg">2</div>
                  <h3 className="text-xl font-black italic uppercase mt-4 mb-3 text-blue-400">
                    {language === 'tr' ? 'Kodu Paylaş' : 'Share the Code'}
                  </h3>
                  <p className="text-white/70 leading-relaxed font-medium">
                    {language === 'tr' ? 'Odada sol üst köşede görünen "Oda ID" veya "Kısa ID" numarasını kopyalayıp katılmasını istediğiniz arkadaşlarınıza gönderin.' : 'Copy the "Room ID" or "Short ID" appearing in the top left corner of the room and send it to the friends you want to join.'}
                  </p>
               </div>

               <div className="bg-white/[0.03] border border-white/10 rounded-3xl p-8 hover:bg-white/[0.05] transition-colors relative">
                  <div className="absolute -top-6 left-8 w-12 h-12 bg-fuchsia-700 text-white font-black text-2xl flex items-center justify-center rounded-full border-4 border-[#020617] shadow-lg">3</div>
                  <h3 className="text-xl font-black italic uppercase mt-4 mb-3 text-fuchsia-400">
                    {language === 'tr' ? 'Arkadaşın Katılsın' : 'Have Your Friend Join'}
                  </h3>
                  <p className="text-white/70 leading-relaxed font-medium">
                    {language === 'tr' ? 'Arkadaşınız ana ekrandan "Arkadaşınla Oyna" butonuna tıklayıp kodu girerek anında masanıza dahil olsun!' : 'Have your friend click the "Play with Friend" button on the home screen and enter the code to instantly join your table!'}
                  </p>
               </div>
            </div>

            <div className="mt-16 flex justify-center">
              <button 
                onClick={() => {
                  safeRequestFullscreen();
                  setLobbyInitialStep('JOIN');
                  setShowLobbyModal(true);
                }}
                className="group relative inline-flex items-center justify-center gap-3 px-10 py-5 bg-cyan-500/20 rounded-2xl font-black text-sm tracking-[0.2em] text-cyan-400 overflow-hidden shadow-[0_0_20px_rgba(6,182,212,0.1)] hover:shadow-[0_0_40px_rgba(6,182,212,0.3)] transition-all active:scale-95 border border-cyan-500/50 hover:bg-cyan-500 hover:text-white"
              >
                  <Users className="relative z-10 w-5 h-5" />
                  <span className="relative z-10">
                    {language === 'tr' ? 'BİR ODAYA KATIL (Mevcut Kodu Gir)' : 'JOIN A ROOM (Enter Existing Code)'}
                  </span>
              </button>
            </div>
          </div>
        </section>

        {/* Nasıl Çalışır / Mimari Section */}
        <section className="py-32 relative overflow-hidden">
          <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-20 items-center">
            <div className="space-y-12">
              <div className="space-y-4">
                <h2 className="text-4xl md:text-5xl font-black tracking-tighter uppercase italic">
                  {language === 'tr' ? 'SİSTEM MİMARİSİ' : 'SYSTEM ARCHITECTURE'}
                </h2>
                <p className="text-white/65 text-lg leading-relaxed">
                  {language === 'tr' ? (
                    <>Pişti.online, adil oyun ve yüksek performansı bir araya getiren <strong>Özel Güvenlik Mimarisi</strong> kullanır. İzole edilmiş bağlantı eşleştirmesi ile cihazlar arası düşük gecikmeli veri akışını birleştirerek mükemmel bir deneyim sunuyoruz.</>
                  ) : (
                    <>Pisti.online uses a <strong>Special Security Architecture</strong> that brings together fair play and high performance. By combining isolated connection matchmaking with low-latency data flow between devices, we offer a perfect experience.</>
                  )}
                </p>
              </div>

              <div className="space-y-6">
                {[
                  { 
                    title: language === 'tr' ? 'Güvenli Eşleştirme' : 'Secure Matchmaking', 
                    text: language === 'tr' ? 'Özel altyapımızla güvenli izolasyonlu odalar oluşturulur.' : 'Secure, isolated rooms are created with our custom infrastructure.' 
                  },
                  { 
                    title: language === 'tr' ? 'Şifreli Kart Dağıtımı' : 'Encrypted Card Dealing', 
                    text: language === 'tr' ? 'Adil oyun için kartlar gelişmiş Anti-Cheat sistemi ile güvenli katmanda karmaşıklaştırılarak dağıtılır.' : 'For fair play, cards are shuffled and dealt securely using an advanced Anti-Cheat system.' 
                  },
                  { 
                    title: language === 'tr' ? 'Düşük Gecikmeli Bağlantı' : 'Low-Latency Connection', 
                    text: language === 'tr' ? 'Oyun anındaki hamleler doğrudan şifrelenmiş kanal üzerinden akarak gecikmeyi sıfıra indirir.' : 'In-game moves flow directly through an encrypted channel, reducing latency to zero.' 
                  }
                ].map((step, i) => (
                  <div key={i} className="flex items-start gap-5 group">
                     <div className="w-10 h-10 shrink-0 rounded-full bg-cyan-500/10 border border-cyan-500/30 flex items-center justify-center font-black text-cyan-400 group-hover:bg-cyan-400 group-hover:text-slate-950 transition-all mt-1">
                       {i + 1}
                     </div>
                     <div>
                       <p className="text-lg font-bold text-white/90">{step.title}</p>
                       <p className="text-sm text-white/65">{step.text}</p>
                     </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="relative p-12 bg-white/[0.02] border border-white/5 rounded-[3rem] backdrop-blur-sm overflow-hidden">
               <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/5 to-transparent" />
               <div className="relative z-10 flex flex-col items-center">
                  <div className="text-center mb-10">
                    <p className="text-cyan-400 font-black tracking-[0.2em] text-sm mb-1 uppercase">
                      {language === 'tr' ? 'ÖZEL MİMARİ' : 'SPECIAL ARCHITECTURE'}
                    </p>
                    <p className="text-[10px] text-white/60 uppercase font-black tracking-widest">
                      {language === 'tr' ? 'GÜVENLİ, HIZLI, MODERN' : 'SECURE, FAST, MODERN'}
                    </p>
                  </div>

                  <div className="relative w-full aspect-video flex items-center justify-center">
                     <div className="absolute inset-0 flex items-center justify-center">
                        <div className="w-40 h-40 border-2 border-emerald-500/20 rounded-full animate-ping opacity-30" />
                        <div className="w-24 h-24 bg-emerald-500/10 border border-emerald-500/40 rounded-3xl flex items-center justify-center shadow-2xl shadow-emerald-500/20">
                           <Shield className="w-10 h-10 text-emerald-400" />
                        </div>
                     </div>

                     {/* Connected Nodes */}
                     {[
                       { pos: 'top-0 left-10', icon: <Monitor /> },
                       { pos: 'top-0 right-10', icon: <Monitor /> },
                       { pos: 'bottom-0 left-1/4', icon: <Smartphone /> },
                       { pos: 'bottom-0 right-1/4', icon: <Laptop /> }
                     ].map((node, i) => (
                       <div key={i} className={cn("absolute w-16 h-12 bg-white/5 border border-white/10 rounded-xl flex items-center justify-center text-white/60 shadow-xl", node.pos)}>
                         {React.cloneElement(node.icon as any, { size: 18 })}
                         <div className="absolute inset-0 border-t border-cyan-400/20 rounded-xl opacity-0 hover:opacity-100 transition-opacity" />
                       </div>
                     ))}
                  </div>

                  <div className="mt-20 grid grid-cols-4 w-full gap-4 opacity-30">
                     <div className="h-1 bg-cyan-500/50 rounded-full" />
                     <div className="h-1 bg-cyan-500/50 rounded-full" />
                     <div className="h-1 bg-emerald-500 rounded-full" />
                     <div className="h-1 bg-cyan-500/50 rounded-full" />
                  </div>
               </div>
            </div>
          </div>
        </section>

        {/* Neden Pisti.online Grid */}
        <section className="py-32 bg-black/40 border-t border-white/5">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-20 space-y-4">
               <h2 className="text-3xl md:text-4xl font-black tracking-tighter uppercase italic">
                 {language === 'tr' ? 'NEDEN PİŞTİ.ONLİNE?' : 'WHY PİŞTİ.ONLINE?'}
               </h2>
               <p className="text-white/65 font-medium tracking-widest uppercase">
                 {language === 'tr' ? 'Profesyonel e-spor platformları seviyesinde kart & masa oyunları altyapısı.' : 'Card & board game infrastructure at the level of professional e-sports platforms.'}
               </p>
            </div>
            
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-8 lg:gap-12">
              {[
                { icon: <Shield />, t: language === 'tr' ? 'ANTİ-CHEAT' : 'ANTI-CHEAT', d: language === 'tr' ? 'Adil oyun ve hile koruması' : 'Fair play and cheat protection' },
                { icon: <Zap />, t: language === 'tr' ? 'DÜŞÜK GECİKME' : 'LOW LATENCY', d: language === 'tr' ? 'Milisaniyelik tepki süresi' : 'Response time in milliseconds' },
                { icon: <Mic className="w-6 h-6" />, t: language === 'tr' ? 'SESLİ SOHBET' : 'VOICE CHAT', d: language === 'tr' ? 'Canlı sesli konuşma' : 'Live voice chat' },
                { icon: <WifiOff className="w-6 h-6" />, t: language === 'tr' ? 'ÇEVRİMDIŞI MOD' : 'OFFLINE MODE', d: language === 'tr' ? 'İnternetsiz yapay zekaya karşı kesintisiz oyna' : 'Play uninterrupted against AI without internet' },
                { icon: <Download className="w-6 h-6" />, t: language === 'tr' ? 'PWA DESTEĞİ' : 'PWA SUPPORT', d: language === 'tr' ? 'Uygulamayı telefon ve bilgisayara anında kur' : 'Install the app instantly on phone and PC' },
                { icon: <Smartphone className="w-6 h-6" />, t: language === 'tr' ? 'MOBİL ÖNCELİKLİ' : 'MOBILE-FIRST', d: language === 'tr' ? 'Tarayıcıdan anında oyna' : 'Play instantly from the browser' }
              ].map((f, i) => (
                <div key={i} className="flex flex-col items-center text-center space-y-4 group">
                  <div className="w-16 h-16 rounded-2xl bg-white/5 border border-white/10 flex items-center justify-center text-cyan-400 group-hover:bg-cyan-400 group-hover:text-slate-950 transition-all transform group-hover:-translate-y-2 group-hover:scale-110">
                     {React.cloneElement(f.icon as any, { size: 28 })}
                  </div>
                  <div>
                     <h3 className="font-black text-sm tracking-widest uppercase italic mb-1">{f.t}</h3>
                     <p className="text-xs text-white/65 uppercase font-bold tracking-widest leading-loose">{f.d}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Yol Haritası Section */}
        <section className="py-32 bg-[#020617] border-t border-white/5 relative overflow-hidden">
          <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-[0.03]" />
          <div className="max-w-7xl mx-auto px-6 relative z-10">
            <div className="text-center mb-20 space-y-4">
               <h2 className="text-3xl md:text-4xl font-black tracking-tighter uppercase italic text-cyan-400">
                 {language === 'tr' ? 'YOL HARİTASI' : 'ROADMAP'}
               </h2>
               <p className="text-white/65 font-medium tracking-widest uppercase text-xs md:text-sm">
                 {language === 'tr' ? 'PİŞTİ, BATAK, KİNG ve OKEY İçin Gelecek Planlarımız' : 'Our Future Plans for PİŞTİ, BATAK, KİNG, and OKEY'}
               </p>
            </div>
            
            <div className="grid lg:grid-cols-3 gap-8">
               <div className="p-8 bg-white/5 rounded-3xl border border-white/10 flex flex-col items-start gap-5 hover:bg-white/10 transition-colors">
                 <div className="px-4 py-1.5 bg-emerald-500/20 text-emerald-400 font-black tracking-widest text-[10px] rounded-full border border-emerald-500/30 uppercase">
                    {language === 'tr' ? 'Tamamlananlar' : 'Completed'}
                 </div>
                 <h3 className="text-2xl font-black text-white italic uppercase">
                   {language === 'tr' ? 'Kart & Masa Oyunları Altyapısı' : 'Card & Board Game Infrastructure'}
                 </h3>
                 <ul className="space-y-4 font-medium text-sm text-white/60 w-full">
                   <li className="flex items-start gap-3">
                     <CheckCircle className="w-5 h-5 text-emerald-400 shrink-0 mt-0.5" /> 
                     <span>
                       {language === 'tr' 
                         ? 'PİŞTİ, BATAK, KİNG ve OKEY çok oyunculu oyun motorları (Multiplayer) ve dahili canlı Sesli Sohbet (Voice Chat) altyapısı' 
                         : 'Multiplayer game engines for PİŞTİ, BATAK, KİNG, and OKEY with built-in live Voice Chat infrastructure'}
                     </span>
                   </li>
                   <li className="flex items-start gap-3">
                     <CheckCircle className="w-5 h-5 text-emerald-400 shrink-0 mt-0.5" /> 
                     <span>
                       {language === 'tr' ? 'Güvenli eşleştirme sistemi' : 'Secure matchmaking system'}
                     </span>
                   </li>
                   <li className="flex items-start gap-3">
                     <CheckCircle className="w-5 h-5 text-emerald-400 shrink-0 mt-0.5" /> 
                     <span>
                       {language === 'tr' ? 'İzole ve şifrelenmiş veri iletimi' : 'Isolated and encrypted data transmission'}
                     </span>
                   </li>
                   <li className="flex items-start gap-3">
                     <CheckCircle className="w-5 h-5 text-emerald-400 shrink-0 mt-0.5" /> 
                     <span>
                       {language === 'tr' 
                         ? 'Gelişmiş yapay zeka destekli bot sistemi sayesinde internetsiz / çevrimdışı (offline) oynayabilme ve gelişmiş PWA (Progressive Web App) desteğiyle her zaman erişim' 
                         : 'Play offline/without internet thanks to advanced AI-supported bot system, and access anytime with advanced PWA (Progressive Web App) support'}
                     </span>
                   </li>
                 </ul>
               </div>

               <div className="p-8 bg-cyan-500/5 rounded-3xl border border-cyan-500/20 flex flex-col items-start gap-5 relative overflow-hidden shadow-[0_0_30px_rgba(34,211,238,0.05)] transform lg:-translate-y-4 border-t-2 border-t-cyan-400">
                 <div className="absolute top-0 right-0 w-32 h-32 bg-cyan-400/10 rounded-full blur-3xl" />
                 <div className="px-4 py-1.5 bg-cyan-500/20 text-cyan-400 font-black tracking-widest text-[10px] rounded-full border border-cyan-500/30 uppercase shadow-[0_0_15px_rgba(34,211,238,0.3)]">
                    {language === 'tr' ? 'Aktif Faz' : 'Active Phase'}
                 </div>
                 <h3 className="text-2xl font-black text-white italic uppercase drop-shadow-md">
                   {language === 'tr' ? 'Turnuva & Lobi Sistemi' : 'Tournament & Lobby System'}
                 </h3>
                 <ul className="space-y-4 font-medium text-sm text-cyan-100/70 w-full">
                   <li className="flex items-start gap-3">
                     <Zap className="w-5 h-5 text-cyan-400 shrink-0 mt-0.5" /> 
                     <span>
                       {language === 'tr' ? 'Genel oyuncu lobisi ve özel sohbet odaları' : 'Global player lobby and private chat rooms'}
                     </span>
                   </li>
                   <li className="flex items-start gap-3">
                     <Zap className="w-5 h-5 text-cyan-400 shrink-0 mt-0.5" /> 
                     <span>
                       {language === 'tr' ? 'Rekabetçi dereceli maçlar ve ELO yetenek sistemi' : 'Competitive ranked matches and ELO rating system'}
                     </span>
                   </li>
                   <li className="flex items-start gap-3">
                     <Zap className="w-5 h-5 text-cyan-400 shrink-0 mt-0.5" /> 
                     <span>
                       {language === 'tr' ? 'Liderlik tabloları (En iyi oyuncular)' : 'Leaderboards (Top players)'}
                     </span>
                   </li>
                   <li className="flex items-start gap-3">
                     <Zap className="w-5 h-5 text-cyan-400 shrink-0 mt-0.5" /> 
                     <span>
                       {language === 'tr' ? 'Gelişmiş SEO optimizasyonlu kart oyunu istatistikleri' : 'Advanced SEO optimized card game statistics'}
                     </span>
                   </li>
                 </ul>
               </div>

               <div className="p-8 bg-white/[0.02] rounded-3xl border border-white/5 flex flex-col items-start gap-5 hover:bg-white/[0.04] transition-colors relative">
                 <div className="px-4 py-1.5 bg-white/10 text-white/65 font-black tracking-widest text-[10px] rounded-full border border-white/10 uppercase">
                    {language === 'tr' ? 'Gelecek Faz' : 'Future Phase'}
                 </div>
                 <h3 className="text-2xl font-black text-white/75 italic uppercase">
                   {language === 'tr' ? 'Mobil & Espor Sürümü' : 'Mobile & Esports Edition'}
                 </h3>
                 <ul className="space-y-4 font-medium text-sm text-white/65 w-full">
                   <li className="flex items-start gap-3">
                     <div className="w-5 h-5 rounded-full border-2 border-white/20 shrink-0 flex items-center justify-center mt-0.5">
                       <div className="w-1.5 h-1.5 bg-white/20 rounded-full"/>
                     </div> 
                     <span>
                       {language === 'tr' ? 'iOS ve Android native mobil uygulamalar' : 'iOS and Android native mobile applications'}
                     </span>
                   </li>
                   <li className="flex items-start gap-3">
                     <div className="w-5 h-5 rounded-full border-2 border-white/20 shrink-0 flex items-center justify-center mt-0.5">
                       <div className="w-1.5 h-1.5 bg-white/20 rounded-full"/>
                     </div> 
                     <span>
                       {language === 'tr' ? 'Ekipler için Kulüp ve Klan sistemleri' : 'Club and Clan systems for teams'}
                     </span>
                   </li>
                   <li className="flex items-start gap-3">
                     <div className="w-5 h-5 rounded-full border-2 border-white/20 shrink-0 flex items-center justify-center mt-0.5">
                       <div className="w-1.5 h-1.5 bg-white/20 rounded-full"/>
                     </div> 
                     <span>
                       {language === 'tr' ? 'Ödüllü e-spor turnuvaları' : 'Award-winning esports tournaments'}
                     </span>
                   </li>
                   <li className="flex items-start gap-3">
                     <div className="w-5 h-5 rounded-full border-2 border-white/20 shrink-0 flex items-center justify-center mt-0.5">
                       <div className="w-1.5 h-1.5 bg-white/20 rounded-full"/>
                     </div> 
                     <span>
                       {language === 'tr' ? 'Parti odaları için yüksek kapasiteli görüntülü konuşma/yayın altyapısı' : 'High-capacity video call/broadcasting infrastructure for party rooms'}
                     </span>
                   </li>
                 </ul>
               </div>
            </div>
          </div>
        </section>
      </main>

         {/* SEO Rich Text & FAQ Section */}
         <section id="seo-faq-section" className="py-24 bg-gradient-to-b from-black/40 to-[#020617] border-t border-white/5 relative overflow-hidden">
           <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/black-thread.png')] opacity-[0.02] pointer-events-none" />
           <div className="max-w-5xl mx-auto px-6 relative z-10 font-sans">
             
             {/* Rich Article Header */}
             <div className="text-center mb-16 space-y-4">
               <h2 className="text-3xl md:text-5xl font-black tracking-tighter uppercase italic text-cyan-400">
                 {language === 'tr' ? 'PİŞTİ OYNA - ONLINE PİŞTİ OYUN REHBERİ' : 'PLAY PİŞTİ - ONLINE PİŞTİ GAME GUIDE'}
               </h2>
               <p className="text-white/65 font-bold tracking-widest uppercase text-xs md:text-sm">
                 {language === 'tr' ? 'Online Pişti Oyunu Hakkında Her Şey ve En Popüler Kart Oyunları' : 'Everything About Online Pisti Game and Most Popular Card Games'}
               </p>
             </div>

             {/* SEO Optimized Rich Content Grid */}
             <div className="grid md:grid-cols-2 gap-10 mb-20 text-white/70 text-sm leading-relaxed font-medium">
               <article className="space-y-6 bg-white/[0.02] border border-white/5 p-8 rounded-3xl backdrop-blur-sm">
                 <h3 className="text-xl font-black italic text-white uppercase border-b border-cyan-500/20 pb-3">
                   {language === 'tr' ? 'Pişti Nedir ve Nasıl Oynanır?' : 'What is Pişti and How to Play?'}
                 </h3>
                 <p>
                   {language === 'tr' ? (
                     <>
                       <strong>Pişti</strong>, Türkiye'nin en sevilen, en dinamik ve oynaması en keyifli iskambil kart oyunlarından biridir. Genellikle 52 kartlık tek bir deste ile oynanan bu eğlenceli <strong>pişti oyunu</strong>, hem taktiksel zeka hem de güçlü bir hafıza gerektirir. Oyunun temel amacı, yerdeki kartları eşleştirerek toplamak, rakiplerinizden daha fazla puana sahip olmak ve en önemlisi yerdeki tek kartı aynı değerde bir kartla alarak <strong>pişti oyna</strong> heyecanını zirveye taşımaktır.
                     </>
                   ) : (
                     <>
                       <strong>Pişti</strong> is one of Turkey's most popular, dynamic, and enjoyable card games. Played with a standard 52-card deck, this exciting <strong>pisti game</strong> requires tactical intelligence and a strong memory. The main objective is to match cards on the table, collect point cards, and most importantly, score a "Pisti" by matching a single card on the pile.
                     </>
                   )}
                 </p>
                 <p>
                   {language === 'tr' ? (
                     <>
                       Pisti.online platformu sayesinde artık hiçbir şey yüklemeden, tarayıcınız üzerinden anında <strong>pişti online</strong> oynamaya başlayabilirsiniz. İster bilgisayarınızdan ister akıllı telefonunuzdan, tek bir tıkla dünyanın her yerindeki oyunculara veya gelişmiş yapay zeka botlarımıza karşı ücretsiz olarak <strong>online pişti</strong> keyfini çıkarın.
                     </>
                   ) : (
                     <>
                       Thanks to the Pisti.online platform, you can now start playing <strong>pisti online</strong> instantly via your browser without downloading anything. Enjoy free <strong>online pisti</strong> against players worldwide or our advanced AI bots on your PC or smartphone.
                     </>
                   )}
                 </p>
               </article>

               <article className="space-y-6 bg-white/[0.02] border border-white/5 p-8 rounded-3xl backdrop-blur-sm">
                 <h3 className="text-xl font-black italic text-white uppercase border-b border-cyan-500/20 pb-3">
                   {language === 'tr' ? 'Pişti Online Avantajları' : 'Advantages of Playing Pişti Online'}
                 </h3>
                 <p>
                   {language === 'tr' ? (
                     <>
                       Klasik kart oyunlarını internete taşırken tüm engelleri kaldırdık. <strong>Bedava pişti oyna</strong> deneyimini arkadaşlarınızla paylaşmanız için geliştirdiğimiz pürüzsüz çok oyunculu altyapımızla, aynı masa etrafındaymış gibi hissedersiniz. Üstelik dahili <strong>sesli sohbet (voice chat)</strong> sayesinde oyun esnasında arkadaşlarınızla canlı olarak konuşabilirsiniz.
                     </>
                   ) : (
                     <>
                       We have removed all obstacles while bringing classic card games online. Our smooth multiplayer infrastructure for a free <strong>pisti oyna</strong> experience makes you feel as if you are around the same table. Furthermore, with built-in <strong>voice chat</strong>, you can talk with your friends live.
                     </>
                   )}
                 </p>
                 <p>
                   {language === 'tr' ? (
                     <>
                       Herhangi bir hile veya suistimale yer vermemek adına geliştirdiğimiz <strong>Anti-Cheat koruması</strong>, şifrelenmiş kart dağıtım algoritmalarımız ve güvenli bağlantı mimarimiz sayesinde adil ve güvenli bir oyun alanı sunuyoruz. Pisti.online sadece bir pişti platformu değil; aynı zamanda <strong>okey oyna</strong>, <strong>batak oyna</strong> ve <strong>king oyna</strong> gibi alternatiflerle tüm masa oyunu severlerin bir araya geldiği modern bir merkezdir.
                     </>
                   ) : (
                     <>
                       We offer a fair and secure gaming environment with our built-in <strong>Anti-Cheat protection</strong>, encrypted card dealing algorithms, and secure connection architecture. Pisti.online is not only a pisti platform but also a modern hub where board game lovers meet with alternatives like <strong>okey</strong>, <strong>batak</strong>, and <strong>king</strong>.
                     </>
                   )}
                 </p>
               </article>
             </div>

             {/* SEO FAQ Structured Layout */}
             <div className="space-y-8 bg-white/[0.01] border border-white/5 p-8 md:p-12 rounded-[2.5rem]">
               <h3 className="text-2xl md:text-3xl font-black italic text-white uppercase text-center mb-10">
                 {language === 'tr' ? 'Sıkça Sorulan Sorular (SSS)' : 'Frequently Asked Questions (FAQ)'}
               </h3>
               
               <div className="grid md:grid-cols-2 gap-8 font-sans">
                 {[
                   {
                     q: language === 'tr' ? 'İnternetten pişti oyna seçeneği ücretsiz mi?' : 'Is playing pişti online free?',
                     a: language === 'tr' 
                       ? 'Evet! pisti.online platformunda pişti, batak, okey ve king oyunlarını oynamak tamamen ücretsizdir. Hiçbir üyelik, abonelik veya gizli ücret ödemeden dilediğiniz gibi oynayabilirsiniz.' 
                       : 'Yes! Playing pisti, batak, okey, and king on pisti.online is completely free. You can play as much as you want without any membership, subscription, or hidden fees.'
                   },
                   {
                     q: language === 'tr' ? 'Arkadaşlarımla pişti online odası kurabilir miyim?' : 'Can I create a private room to play pişti online with friends?',
                     a: language === 'tr'
                       ? 'Kesinlikle! "Arkadaşınla Oyna" (P2P) modunu seçerek kendinize özel bir oyun odası kurabilir, size verilen kısa oda kodunu arkadaşınızla paylaşarak anında aynı masada buluşup oynamaya başlayabilirsiniz.'
                       : 'Absolutely! By selecting "Play with Friends" (P2P) mode, you can host your private game room, share the short room code with your friends, and start playing on the same table instantly.'
                   },
                   {
                     q: language === 'tr' ? 'Pişti oyna esnasında sesli sohbet nasıl kullanılır?' : 'How do I use voice chat during gameplay?',
                     a: language === 'tr'
                       ? 'Masanıza katıldıktan sonra ekranın sol tarafında bulunan mikrofon simgesine tıklayarak sesli sohbeti anında aktif hale getirebilirsiniz. Tamamen tarayıcı içi çalışan bu teknoloji için ekstra bir program (Discord, Teamspeak vb.) kurmanıza gerek yoktur.'
                       : 'Once you join your table, you can click on the microphone icon located on the screen to activate live voice chat instantly. This in-browser feature does not require any external apps like Discord.'
                   },
                   {
                     q: language === 'tr' ? 'İnternetim olmadığında pişti oynamaya devam edebilir miyim?' : 'Can I play pişti offline without an internet connection?',
                     a: language === 'tr'
                       ? 'Evet! İnternet bağlantınız koptuğunda veya çevrimdışı olduğunuzda, gelişmiş yapay zekaya sahip akıllı botlarımıza karşı kesintisiz çevrimdışı (offline) pişti oynamaya devam edebilirsiniz. Botlarımız her seviyeden oyuncuya hitap edecek şekilde optimize edilmiştir.'
                       : 'Yes! When you lose internet connection or want to play offline, you can play uninterrupted against our smart AI bots. Our bots are optimized to offer a challenge for players of all levels.'
                   }
                  ].map((faq, idx) => (
                    <div key={idx} className="bg-white/5 border border-white/5 p-6 rounded-2xl hover:border-cyan-500/30 transition-all group">
                      <h4 className="font-black text-base text-cyan-400 group-hover:text-cyan-300 transition-colors mb-2 flex items-start gap-2">
                        <span className="text-white/60 font-mono">Q.</span>
                        <span>{faq.q}</span>
                      </h4>
                      <p className="text-xs md:text-sm text-white/70 leading-relaxed font-medium pl-6">
                        {faq.a}
                      </p>
                    </div>
                  ))}
                </div>
              </div>

            </div>
          </section>

      {/* Footer */}
      <footer className="relative pt-20 pb-8 border-t border-white/10 bg-[#020617]/80 backdrop-blur-md overflow-hidden">
        {/* Glow Effects */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full max-w-4xl h-px bg-gradient-to-r from-transparent via-cyan-500/50 to-transparent" />
        <div className="absolute top-0 left-1/4 w-64 h-64 bg-cyan-500/5 rounded-full blur-[80px]" />
        <div className="absolute bottom-0 right-1/4 w-64 h-64 bg-blue-600/5 rounded-full blur-[80px]" />

        <div className="relative max-w-7xl mx-auto px-6 z-10">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 lg:gap-8 mb-16">
            
            {/* 1. Logo & Açıklama */}
            <div className="space-y-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-cyan-500/10 border border-cyan-500/30 flex items-center justify-center shadow-[0_0_15px_rgba(34,211,238,0.2)]">
                  <Shield className="w-6 h-6 text-cyan-400" />
                </div>
                <div>
                  <h3 className="text-xl font-black tracking-tighter leading-none text-white">Pişti.online</h3>
                  <p className="text-[10px] text-cyan-400 font-bold tracking-[0.2em] uppercase mt-1">
                    {language === 'tr' ? 'GÜVENLİ AĞ ÜZERİNDEN GERÇEK ZAMANLI' : 'REAL-TIME OVER SECURE NETWORK'}
                  </p>
                </div>
              </div>
              <p className="text-sm text-white/65 leading-relaxed font-medium">
                {language === 'tr' 
                  ? 'PİŞTİ, BATAK, KİNG ve OKEY oyunlarını sıfır gecikmeli izole bağlantı sistemi üzerinden güvenle oyna.' 
                  : 'Play PİŞTİ, BATAK, KİNG, and OKEY games safely over our zero-latency isolated connection system.'}
              </p>
            </div>

            {/* 2. Navigasyon */}
            <div className="space-y-6 lg:pl-8">
              <h4 className="text-xs font-black text-white tracking-[0.2em] uppercase">
                {language === 'tr' ? 'Menü' : 'Menu'}
              </h4>
              <nav className="flex flex-col gap-3">
                <a href="#" onClick={(e) => { e.preventDefault(); window.scrollTo({ top: 0, behavior: 'smooth' }); }} className="text-sm text-white/65 hover:text-cyan-400 transition-colors w-fit font-medium">
                  {language === 'tr' ? 'Ana Sayfa' : 'Home'}
                </a>
                <a href="#" onClick={(e) => { e.preventDefault(); const gamesSection = document.getElementById('oyunlar-section'); if(gamesSection) gamesSection.scrollIntoView({ behavior: 'smooth' }); }} className="text-sm text-white/65 hover:text-cyan-400 transition-colors w-fit font-medium">
                  {language === 'tr' ? 'Oyunlar' : 'Games'}
                </a>
                <a href="#" onClick={(e) => { e.preventDefault(); setShowHowToPlayModal(true); }} className="text-sm text-white/65 hover:text-cyan-400 transition-colors w-fit font-medium">
                  {language === 'tr' ? 'Nasıl Oynanır' : 'How to Play'}
                </a>
                <a href="#" onClick={(e) => { e.preventDefault(); setShowP2PModal(true); }} className="text-sm text-white/65 hover:text-cyan-400 transition-colors w-fit font-medium">
                  {language === 'tr' ? 'Bağlantı Mimarisi' : 'Connection Architecture'}
                </a>
                <button onClick={() => setShowContactModal(true)} className="text-sm text-white/65 hover:text-cyan-400 transition-colors w-fit font-medium text-left">
                  {language === 'tr' ? 'İletişim' : 'Contact'}
                </button>
              </nav>
            </div>

            {/* 3. Oyunlar */}
            <div className="space-y-6">
              <h4 className="text-xs font-black text-white tracking-[0.2em] uppercase">
                {language === 'tr' ? 'Oyunlar' : 'Games'}
              </h4>
              <nav className="flex flex-col gap-3">
                {['Pişti', 'Batak', 'KİNG', 'Okey'].map((item) => (
                  <a key={item} href="#" onClick={(e) => { e.preventDefault(); openLobbyWithGame(item); }} className="text-sm text-white/65 hover:text-cyan-400 transition-colors w-fit pr-4 font-medium flex items-center gap-2 group">
                    <span className="w-1.5 h-1.5 rounded-full bg-cyan-500/30 group-hover:bg-cyan-400 transition-colors" />
                    {item === 'Okey' ? (
                      <span className="flex items-center gap-1 hover:text-amber-300 text-amber-400 transition-colors">
                        OKEY
                      </span>
                    ) : item}
                  </a>
                ))}
              </nav>
            </div>

            {/* 4. İletişim */}
            <div className="space-y-6">
              <h4 className="text-xs font-black text-white tracking-[0.2em] uppercase">
                {language === 'tr' ? 'İletişim' : 'Contact'}
              </h4>
              <div className="flex flex-col gap-4 text-sm text-white/65 font-medium">
                <div className="flex items-start gap-3">
                  <Globe className="w-4 h-4 text-cyan-400 mt-1 shrink-0" />
                  <span>
                    {language === 'tr' ? 'Adres: Ankara, Türkiye' : 'Address: Ankara, Turkey'}
                  </span>
                </div>
                <div className="flex items-start gap-3">
                  <MessageSquare className="w-4 h-4 text-cyan-400 mt-1 shrink-0" />
                  <button onClick={() => setShowContactModal(true)} className="hover:text-cyan-400 transition-colors text-left flex items-center gap-1.5">
                    {language === 'tr' ? 'Email: mail@pisti.online' : 'Email: mail@pisti.online'}
                  </button>
                </div>
              </div>
            </div>

          </div>

          <div className="mt-8 mb-10">
            <SocialShare />
          </div>

          {/* Footer Backlink Card Banner Component (iskambil.online) */}
          <IskambilBacklinkBanner variant="footer" />

          <div className="h-px w-full bg-gradient-to-r from-transparent via-white/10 to-transparent mb-8" />

          {/* 5. Alt bar */}
          <div className="flex flex-col md:flex-row items-center justify-between gap-6 mb-8">
            <p className="text-[11px] text-white/60 font-bold tracking-widest uppercase">
              {language === 'tr' ? '© 2026 Pişti.online – Tüm hakları saklıdır.' : '© 2026 Pişti.online – All rights reserved.'}
            </p>
            <div className="flex items-center gap-6 text-white/60 relative z-20">
              <button type="button" onClick={(e) => { e.preventDefault(); props.onOpenTermsOfUse(); }} className="text-[10px] font-bold tracking-widest uppercase hover:text-cyan-400 transition-colors cursor-pointer relative">
                {language === 'tr' ? 'Kullanım Koşulları' : 'Terms of Use'}
              </button>
              <button type="button" onClick={(e) => { e.preventDefault(); props.onOpenCookiePolicy(); }} className="text-[10px] font-bold tracking-widest uppercase hover:text-cyan-400 transition-colors cursor-pointer relative">
                {language === 'tr' ? 'Çerez Politikası' : 'Cookie Policy'}
              </button>
              <span title={language === 'tr' ? 'Hızlı Düzenbaz Mimarisi' : 'Fast Rogues Architecture'}><Globe className="w-4 h-4 hover:text-cyan-400 transition-colors cursor-help" /></span>
              <span title={language === 'tr' ? 'Güvenlik' : 'Security'}><Shield className="w-4 h-4 hover:text-cyan-400 transition-colors cursor-help" /></span>
              <span title={language === 'tr' ? 'Düşük Gecikme' : 'Low Latency'}><Zap className="w-4 h-4 hover:text-cyan-400 transition-colors cursor-help" /></span>
            </div>
          </div>

          {/* 6. SEO Micro Text & 7. 18+ Warning */}
          <div className="flex flex-col items-center gap-4 text-center">
            <p className="text-[9px] text-white/55 leading-relaxed font-mono max-w-4xl flex flex-wrap justify-center gap-x-3 gap-y-1">
              {language === 'tr' ? (
                <>
                  <span>pişti oyunu</span> <span>&bull;</span>
                  <span>online batak</span> <span>&bull;</span>
                  <span>online king oyunu</span> <span>&bull;</span>
                  <span>online okey</span> <span>&bull;</span>
                  <span>güvenli çok oyunculu kart ve masa platformu</span> <span>&bull;</span>
                  <span>pisti.online ile kart & masa oyunları</span> <span>&bull;</span>
                  <span>sıfır gecikmeli rekabetçi pişti</span> <span>&bull;</span>
                  <span>arkadaşlarla pişti ve batak ihaleli</span>
                </>
              ) : (
                <>
                  <span>pisti game</span> <span>&bull;</span>
                  <span>online batak</span> <span>&bull;</span>
                  <span>online king game</span> <span>&bull;</span>
                  <span>online okey</span> <span>&bull;</span>
                  <span>secure multiplayer card & board platform</span> <span>&bull;</span>
                  <span>card & board games with pisti.online</span> <span>&bull;</span>
                  <span>zero-latency competitive pisti</span> <span>&bull;</span>
                  <span>pisti and bidding batak with friends</span>
                </>
              )}
            </p>
            
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              viewport={{ once: true }}
              className="w-full max-w-2xl mx-auto mt-6"
            >
              <div className="h-px w-full bg-gradient-to-r from-transparent via-white/10 to-transparent mb-6" />

              <div className="flex flex-col items-center justify-center gap-3">
              </div>
            </motion.div>
          </div>

        </div>
      </footer>

      {/* How To Play Modal */}
      <AnimatePresence>
        {showHowToPlayModal && (
          <div className="fixed inset-0 z-[400] flex items-center justify-center p-4">
             <motion.div 
               initial={{ opacity: 0 }}
               animate={{ opacity: 1 }}
               exit={{ opacity: 0 }}
               onClick={() => setShowHowToPlayModal(false)}
               className="absolute inset-0 bg-[#020617]/95 backdrop-blur-xl"
             />
             <motion.div 
               initial={{ scale: 0.95, opacity: 0, y: 30 }}
               animate={{ scale: 1, opacity: 1, y: 0 }}
               exit={{ scale: 0.95, opacity: 0, y: 30 }}
               transition={{ type: "spring", stiffness: 300, damping: 30 }}
               className="relative w-full max-w-4xl max-h-[85vh] bg-slate-900/80 border border-white/10 rounded-[2.5rem] overflow-hidden shadow-[0_0_80px_rgba(34,211,238,0.15)] backdrop-blur-2xl flex flex-col"
             >
                <div className="flex items-center justify-between p-6 md:p-8 border-b border-white/10 bg-white/5">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-cyan-500/10 border border-cyan-500/30 flex items-center justify-center shadow-inner pt-1 pl-0.5">
                      <Info className="w-6 h-6 text-cyan-400" />
                    </div>
                    <div>
                      <h2 className="text-2xl md:text-3xl font-black italic tracking-tighter uppercase text-white">
                        {language === 'tr' ? 'Nasıl Oynanır?' : 'How to Play?'}
                      </h2>
                      <p className="text-[10px] uppercase tracking-[0.2em] text-cyan-400/70 font-bold">
                        {language === 'tr' ? 'Oyun Kuralları ve Taktikler' : 'Rules and Tactics'}
                      </p>
                    </div>
                  </div>
                  <button 
                    onClick={() => setShowHowToPlayModal(false)}
                    aria-label={language === 'tr' ? 'Kapat' : 'Close'}
                    className="p-3 bg-white/5 hover:bg-white/10 rounded-xl text-white/40 hover:text-white transition-all border border-white/5"
                  >
                    <X className="w-6 h-6" />
                  </button>
                </div>
                
                <div className="flex-1 overflow-y-auto p-6 md:p-8 space-y-12 overscroll-contain text-white">
                  {/* Pişti Section */}
                  <div className="space-y-6">
                    <div className="flex items-center gap-4 border-b border-white/10 pb-4">
                      <Layers className="w-8 h-8 text-emerald-400" />
                      <h3 className="text-3xl font-black tracking-tighter italic uppercase text-emerald-400">PİŞTİ</h3>
                    </div>
                    <div className="grid md:grid-cols-2 gap-8">
                      <div className="space-y-4">
                        <h4 className="text-sm font-black tracking-widest uppercase text-white/50">
                          {language === 'tr' ? 'Temel Kurallar' : 'Basic Rules'}
                        </h4>
                        <ul className="space-y-3 text-sm text-white/80 leading-relaxed font-medium">
                          {language === 'tr' ? (
                            <>
                              <li className="flex items-start gap-2"><span className="text-emerald-400 font-bold mt-0.5">•</span> <span>4 kişi (teklif veya eşli) oynanır. Oyunun amacı puanlı kağıtları toplamak ve "Pişti" yapmaktır.</span></li>
                              <li className="flex items-start gap-2"><span className="text-emerald-400 font-bold mt-0.5">•</span> <span>Yerdeki en üstteki kart ile aynı değerde bir kart atarsanız veya "Vale (J)" atarsanız yerdeki tüm kartları alırsınız.</span></li>
                              <li className="flex items-start gap-2"><span className="text-emerald-400 font-bold mt-0.5">•</span> <span>Yerde sadece tek bir kart varken, o kartın aynısını atarsanız "Pişti" yapmış olursunuz.</span></li>
                            </>
                          ) : (
                            <>
                              <li className="flex items-start gap-2"><span className="text-emerald-400 font-bold mt-0.5">•</span> <span>Played with 4 players (solo or partners). The objective is to collect point cards and score "Pisti".</span></li>
                              <li className="flex items-start gap-2"><span className="text-emerald-400 font-bold mt-0.5">•</span> <span>If you play a card of the same value as the top card on the pile, or a "Jack (J)", you capture all the cards on the table.</span></li>
                              <li className="flex items-start gap-2"><span className="text-emerald-400 font-bold mt-0.5">•</span> <span>If there is only a single card on the pile and you match it exactly, you score a "Pisti".</span></li>
                            </>
                          )}
                        </ul>
                      </div>
                      <div className="space-y-4">
                        <h4 className="text-sm font-black tracking-widest uppercase text-white/50">
                          {language === 'tr' ? 'Puanlama' : 'Scoring'}
                        </h4>
                        <div className="grid grid-cols-2 gap-3">
                          <div className="bg-white/5 p-4 rounded-xl border border-white/5 flex flex-col items-center justify-center text-center gap-1">
                            <span className="text-2xl font-black text-white">10</span>
                            <span className="text-[10px] uppercase font-bold tracking-widest text-emerald-400">
                              {language === 'tr' ? 'Pişti' : 'Pisti'}
                            </span>
                          </div>
                          <div className="bg-white/5 p-4 rounded-xl border border-white/5 flex flex-col items-center justify-center text-center gap-1">
                            <span className="text-2xl font-black text-white">20</span>
                            <span className="text-[10px] uppercase font-bold tracking-widest text-emerald-400">
                              {language === 'tr' ? 'J ile Pişti' : 'Pisti with J'}
                            </span>
                          </div>
                          <div className="bg-white/5 p-4 rounded-xl border border-white/5 flex items-center justify-between text-left col-span-2 px-6">
                            <span className="text-xs uppercase font-bold tracking-widest text-white/60">
                              {language === 'tr' ? 'As (A) & Vale (J)' : 'Ace (A) & Jack (J)'}
                            </span>
                            <span className="text-lg font-black text-emerald-400">1<span className="text-xs">P</span></span>
                          </div>
                          <div className="bg-white/5 p-4 rounded-xl border border-white/5 flex items-center justify-between text-left col-span-2 px-6">
                            <span className="text-xs uppercase font-bold tracking-widest text-white/60">
                              {language === 'tr' ? 'Sinek 2' : '2 of Clubs'}
                            </span>
                            <span className="text-lg font-black text-emerald-400">2<span className="text-xs">P</span></span>
                          </div>
                          <div className="bg-white/5 p-4 rounded-xl border border-white/5 flex items-center justify-between text-left col-span-2 px-6">
                            <span className="text-xs uppercase font-bold tracking-widest text-white/60">
                              {language === 'tr' ? 'Karo 10' : '10 of Diamonds'}
                            </span>
                            <span className="text-lg font-black text-emerald-400">3<span className="text-xs">P</span></span>
                          </div>
                          <div className="bg-white/5 p-4 rounded-xl border border-white/5 flex items-center justify-between text-left col-span-2 px-6">
                            <span className="text-xs uppercase font-bold tracking-widest text-white/60">
                              {language === 'tr' ? 'Kart Fazlalığı' : 'Most Cards'}
                            </span>
                            <span className="text-lg font-black text-emerald-400">3<span className="text-xs">P</span></span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Batak Section */}
                  <div className="space-y-6">
                    <div className="flex items-center gap-4 border-b border-white/10 pb-4">
                      <Play className="w-8 h-8 text-amber-400 fill-current" />
                      <h3 className="text-3xl font-black tracking-tighter italic uppercase text-amber-400">Batak</h3>
                    </div>
                    <div className="grid md:grid-cols-2 gap-8">
                      <div className="space-y-4">
                        <h4 className="text-sm font-black tracking-widest uppercase text-white/50">
                          {language === 'tr' ? 'Oynanış (İhaleli)' : 'Gameplay (Bidding)'}
                        </h4>
                        <ul className="space-y-3 text-sm text-white/80 leading-relaxed font-medium">
                          {language === 'tr' ? (
                            <>
                              <li className="flex items-start gap-2"><span className="text-amber-400 font-bold mt-0.5">•</span> <span>Herkes alabileceği el sayısını tahmin ederek bir ihaleye girer. En yüksek tahmini yapan ihaleyi kazanır ve Koz'u o belirler.</span></li>
                              <li className="flex items-start gap-2"><span className="text-amber-400 font-bold mt-0.5">•</span> <span>Oyun sırasında yere atılan ilk kartın cinsinden kart atmak zorunludur. Eğer elinizde yoksa, "Koz" atarak eli alabilirsiniz.</span></li>
                              <li className="flex items-start gap-2"><span className="text-amber-400 font-bold mt-0.5">•</span> <span>İhaleyi alan kişi, taahhüt ettiği el sayısına ulaşamazsa, ihale miktarı kadar eksi puan alır (Batar).</span></li>
                            </>
                          ) : (
                            <>
                              <li className="flex items-start gap-2"><span className="text-amber-400 font-bold mt-0.5">•</span> <span>Everyone bids on how many hands they can win. The highest bidder wins the bid and declares the Trump.</span></li>
                              <li className="flex items-start gap-2"><span className="text-amber-400 font-bold mt-0.5">•</span> <span>You must play a card of the leading suit if you have one. If not, you can play a "Trump" to capture the hand.</span></li>
                              <li className="flex items-start gap-2"><span className="text-amber-400 font-bold mt-0.5">•</span> <span>If the bidder fails to reach their promised number of hands, they receive negative points equal to their bid (Busted).</span></li>
                            </>
                          )}
                        </ul>
                      </div>
                      <div className="space-y-4">
                        <h4 className="text-sm font-black tracking-widest uppercase text-white/50">
                          {language === 'tr' ? 'Varyasyonlar' : 'Variants'}
                        </h4>
                        <div className="grid grid-cols-1 gap-3">
                          <div className="bg-white/5 p-4 rounded-xl border border-white/5">
                            <span className="block text-sm font-black text-amber-400 uppercase tracking-widest mb-1">
                              {language === 'tr' ? 'Koz Maça' : 'Trump Spades'}
                            </span>
                            <span className="text-xs text-white/60 font-medium">
                              {language === 'tr' 
                                ? "Koz her zaman maça'dır. İhale yoktur, herkes alacağını düşündüğü eli söyler. Dediği eli alamayan veya dediğinden 3 fazla alan batar." 
                                : "Trump is always Spades. There is no bidding; everyone declares their estimated hands. Players bust if they fail to win their declared hands or win 3 more than declared."}
                            </span>
                          </div>
                          <div className="bg-white/5 p-4 rounded-xl border border-white/5">
                            <span className="block text-sm font-black text-amber-400 uppercase tracking-widest mb-1">
                              {language === 'tr' ? 'Gömmeli Batak' : 'Buried Batak'}
                            </span>
                            <span className="text-xs text-white/60 font-medium">
                              {language === 'tr' 
                                ? '3 kişiyle oynanır, yere 4 kağıt gömülür. İhaleyi alan kişi yerdeki kağıtları kendi eline alır ve elinden 4 kağıt çıkarır.' 
                                : 'Played with 3 players; 4 cards are buried on the table. The bidder takes these buried cards into their hand and discards 4 cards.'}
                            </span>
                          </div>
                          <div className="bg-white/5 p-4 rounded-xl border border-white/5">
                            <span className="block text-sm font-black text-amber-400 uppercase tracking-widest mb-1">
                              {language === 'tr' ? 'Eşli İhaleli' : 'Partnership Batak'}
                            </span>
                            <span className="text-xs text-white/60 font-medium">
                              {language === 'tr' 
                                ? "İhaleyi alan kişinin eşinin kâğıtları yere açılır ve ihaleyi alan kişi tarafından oynanır." 
                                : "The bidder's partner's cards are opened face-up on the table and played by the bidder."}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* King Section */}
                  <div className="space-y-6">
                    <div className="flex items-center gap-4 border-b border-white/10 pb-4">
                      <Shield className="w-8 h-8 text-purple-400" />
                      <h3 className="text-3xl font-black tracking-tighter italic uppercase text-purple-400">KİNG</h3>
                    </div>
                    <div className="grid md:grid-cols-2 gap-8">
                      <div className="space-y-4">
                         <h4 className="text-sm font-black tracking-widest uppercase text-white/50">
                           {language === 'tr' ? 'Genel Kurallar' : 'General Rules'}
                         </h4>
                         <ul className="space-y-3 text-sm text-white/80 leading-relaxed font-medium">
                           {language === 'tr' ? (
                             <>
                               <li className="flex items-start gap-2"><span className="text-purple-400 font-bold mt-0.5">•</span> <span>King oyunu, her oyuncunun belirli elleri seçtiği toplam 20 elden oluşur (12 Ceza, 8 Koz).</span></li>
                               <li className="flex items-start gap-2"><span className="text-purple-400 font-bold mt-0.5">•</span> <span>Her oyun başında, sırası gelen bir oyuncu o elin "Ceza" mı yoksa "Koz" mu oynanacağını seçer.</span></li>
                               <li className="flex items-start gap-2"><span className="text-purple-400 font-bold mt-0.5">•</span> <span>Koz oyunlarında amaç en çok eli almakken, Ceza oyunlarında amaç seçilen renkteki (veya karttaki) kartları almamaktır.</span></li>
                             </>
                           ) : (
                             <>
                               <li className="flex items-start gap-2"><span className="text-purple-400 font-bold mt-0.5">•</span> <span>King consists of 20 hands total, where each player chooses specific contracts (12 Penalties, 8 Trumps).</span></li>
                               <li className="flex items-start gap-2"><span className="text-purple-400 font-bold mt-0.5">•</span> <span>At the start of each round, the active player chooses whether that hand will be played as a "Penalty" or "Trump".</span></li>
                               <li className="flex items-start gap-2"><span className="text-purple-400 font-bold mt-0.5">•</span> <span>In trump games, the goal is to win as many hands as possible, while in penalty games, the goal is to avoid taking the specified cards or suits.</span></li>
                             </>
                           )}
                         </ul>
                      </div>
                      <div className="space-y-4">
                         <h4 className="text-sm font-black tracking-widest uppercase text-white/50">
                           {language === 'tr' ? 'Cezalar' : 'Penalties'}
                         </h4>
                         <div className="grid grid-cols-2 gap-3 text-xs font-medium">
                           <div className="bg-white/5 p-3 rounded-xl border border-white/5">
                             <span className="block font-black text-purple-400 mb-0.5 uppercase tracking-wide">
                               {language === 'tr' ? 'El Almaz' : 'No Tricks'}
                             </span>
                             <span className="text-white/60">
                               {language === 'tr' ? 'Mümkün olduğunca az el alınmalıdır.' : 'Avoid taking any hands if possible.'}
                             </span>
                           </div>
                           <div className="bg-white/5 p-3 rounded-xl border border-white/5">
                             <span className="block font-black text-purple-400 mb-0.5 uppercase tracking-wide">
                               {language === 'tr' ? 'Kupa Almaz' : 'No Hearts'}
                             </span>
                             <span className="text-white/60">
                               {language === 'tr' ? 'Kupa renkli kartlar alınmamalıdır.' : 'Avoid taking Heart cards.'}
                             </span>
                           </div>
                           <div className="bg-white/5 p-3 rounded-xl border border-white/5">
                             <span className="block font-black text-purple-400 mb-0.5 uppercase tracking-wide">
                               {language === 'tr' ? 'Erkek Almaz' : 'No Kings/Jacks'}
                             </span>
                             <span className="text-white/60">
                               {language === 'tr' ? 'Papaz (K) ve Vale (J) alınmamalıdır.' : 'Avoid taking Kings (K) and Jacks (J).'}
                             </span>
                           </div>
                           <div className="bg-white/5 p-3 rounded-xl border border-white/5">
                             <span className="block font-black text-purple-400 mb-0.5 uppercase tracking-wide">
                               {language === 'tr' ? 'Kız Almaz' : 'No Queens'}
                             </span>
                             <span className="text-white/60">
                               {language === 'tr' ? 'Kız (Q) kartları alınmamalıdır.' : 'Avoid taking Queens (Q).'}
                             </span>
                           </div>
                           <div className="bg-white/5 p-3 rounded-xl border border-white/5 col-span-2">
                             <span className="block font-black text-purple-400 mb-0.5 uppercase tracking-wide">
                               {language === 'tr' ? 'Son İki' : 'Last Two'}
                             </span>
                             <span className="text-white/60">
                               {language === 'tr' ? 'Oyunun son iki eli alınmamalıdır.' : 'Avoid taking the last two hands of the round.'}
                             </span>
                           </div>
                           <div className="bg-white/5 p-3 rounded-xl border border-white/5 col-span-2 flex items-center gap-3">
                             <span className="font-black text-purple-400 uppercase tracking-wide">
                               {language === 'tr' ? 'Rıfkı' : 'Rıfkı (No King of H)'}
                             </span>
                             <span className="text-white/60 flex-1">
                               {language === 'tr' ? 'Kupa Papazı (K) alınmamalıdır.' : 'Avoid taking the King of Hearts.'}
                             </span>
                           </div>
                         </div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Okey Section */}
                  <div className="space-y-6">
                    <div className="flex items-center gap-4 border-b border-white/10 pb-4">
                      <LayoutGrid className="w-8 h-8 text-amber-400" />
                      <h3 className="text-3xl font-black tracking-tighter italic uppercase text-amber-400">OKEY</h3>
                    </div>
                    <div className="grid md:grid-cols-2 gap-8">
                      <div className="space-y-4">
                         <h4 className="text-sm font-black tracking-widest uppercase text-white/50">
                           {language === 'tr' ? 'Temel Kurallar' : 'Basic Rules'}
                         </h4>
                         <ul className="space-y-3 text-sm text-white/80 leading-relaxed font-medium">
                           {language === 'tr' ? (
                             <>
                               <li className="flex items-start gap-2"><span className="text-amber-400 font-bold mt-0.5">•</span> <span>106 taş ve 4 ıstaka ile oynanır. Oyunun amacı perleri (seri veya aynı numaralı farklı renkler) oluşturmaktır.</span></li>
                               <li className="flex items-start gap-2"><span className="text-amber-400 font-bold mt-0.5">•</span> <span>İlk oyuncuya 15, diğer oyunculara 14 taş dağıtılır.</span></li>
                               <li className="flex items-start gap-2"><span className="text-amber-400 font-bold mt-0.5">•</span> <span>14 taşını kurallara uygun olarak per yapan oyuncu elindeki 15. taşı ortaya atarak eli kazanır veya gösterge gösterir.</span></li>
                             </>
                           ) : (
                             <>
                               <li className="flex items-start gap-2"><span className="text-amber-400 font-bold mt-0.5">•</span> <span>Played with 106 tiles and 4 racks. The objective is to form groups or runs of tiles (sets/runs).</span></li>
                               <li className="flex items-start gap-2"><span className="text-amber-400 font-bold mt-0.5">•</span> <span>The first player is dealt 15 tiles, while other players receive 14.</span></li>
                               <li className="flex items-start gap-2"><span className="text-amber-400 font-bold mt-0.5">•</span> <span>A player who completes their sets wins by discarding their 15th tile, or shows the indicator tile.</span></li>
                             </>
                           )}
                         </ul>
                      </div>
                    </div>
                  </div>

                </div>
                
                <div className="p-6 border-t border-white/5 flex flex-col sm:flex-row justify-end gap-3 bg-[#020617]/50">
                  <button
                     onClick={() => setShowHowToPlayModal(false)}
                     className="flex items-center justify-center gap-2 px-8 py-3 w-full sm:w-auto bg-white/5 border border-white/10 hover:bg-white/10 text-white font-black uppercase tracking-widest text-sm rounded-xl transition-all"
                  >
                     {language === 'tr' ? 'Geri Dön' : 'Go Back'}
                  </button>
                </div>
             </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* P2P Modal */}
      <AnimatePresence>
        {showP2PModal && (
          <div className="fixed inset-0 z-[400] flex items-center justify-center p-4">
             <motion.div 
               initial={{ opacity: 0 }}
               animate={{ opacity: 1 }}
               exit={{ opacity: 0 }}
               onClick={() => setShowP2PModal(false)}
               className="absolute inset-0 bg-[#020617]/95 backdrop-blur-xl"
             />
             <motion.div 
               initial={{ scale: 0.95, opacity: 0, y: 30 }}
               animate={{ scale: 1, opacity: 1, y: 0 }}
               exit={{ scale: 0.95, opacity: 0, y: 30 }}
               transition={{ type: "spring", stiffness: 300, damping: 30 }}
               className="relative w-full max-w-2xl max-h-[85dvh] lg:max-h-[85vh] bg-slate-900 border border-white/10 rounded-[2rem] overflow-hidden shadow-[0_0_50px_rgba(34,211,238,0.1)] flex flex-col"
             >
                <div className="flex items-center justify-between p-6 md:p-8 border-b border-white/10 bg-white/5 shrink-0">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-cyan-500/10 border border-cyan-500/30 flex items-center justify-center shadow-inner pt-1 pl-0.5">
                      <Globe className="w-6 h-6 text-cyan-400" />
                    </div>
                    <div>
                      <h2 className="text-xl md:text-2xl font-black italic tracking-tighter uppercase text-white">
                        {language === 'tr' ? 'MİMARİ' : 'ARCHITECTURE'}
                      </h2>
                      <p className="text-[10px] uppercase tracking-[0.2em] text-cyan-400/70 font-bold">
                        {language === 'tr' ? 'GÜVENLİ VE İZOLE AĞ' : 'SECURE & ISOLATED NETWORK'}
                      </p>
                    </div>
                  </div>
                  <button 
                    onClick={() => setShowP2PModal(false)}
                    aria-label={language === 'tr' ? 'Kapat' : 'Close'}
                    className="p-3 bg-white/5 hover:bg-white/10 rounded-xl text-white/40 hover:text-white transition-all border border-white/5"
                  >
                    <X className="w-6 h-6" />
                  </button>
                </div>
                
                <div className="p-6 md:p-8 space-y-6 text-sm text-white/70 leading-relaxed font-medium flex-1 overflow-y-auto">
                  <p>
                    {language === 'tr' ? (
                      <><strong className="text-cyan-400">Gizli Sinyalleme</strong> oyun modu, temel güvenlik ve eşleştirme katmanını aktif bir şekilde güvenceye alıp, yüksek hızlı veri akışının doğrudan özel cihaz bağlantıları arasından gerçekleştirildiği modern bir güvenlik mimarisidir.</>
                    ) : (
                      <><strong className="text-cyan-400">Hidden Signaling</strong> game mode is a modern security architecture that actively secures the basic security and matchmaking layer, while high-speed data flow is carried out directly between private device connections.</>
                    )}
                  </p>
                  
                  <div className="grid md:grid-cols-2 gap-4">
                     <div className="bg-white/5 border border-white/5 rounded-2xl p-4 space-y-2">
                       <h4 className="text-white font-bold uppercase tracking-widest text-xs flex items-center gap-2">
                         <Zap className="w-4 h-4 text-emerald-400" /> 
                         {language === 'tr' ? 'Avantajları' : 'Advantages'}
                       </h4>
                       <ul className="space-y-2 text-white/50 text-xs">
                         {language === 'tr' ? (
                           <>
                             <li className="flex gap-2"><span className="text-emerald-400">•</span> Mükemmel düşük gecikme (ping) süresi ve senkronizasyon.</li>
                             <li className="flex gap-2"><span className="text-emerald-400">•</span> Trafiğe takılmadan hızlı ve akıcı kart hamleleri imkanı.</li>
                             <li className="flex gap-2"><span className="text-emerald-400">•</span> Gönderilen içerikler şifrelenmiş kanalın limitlerinden asla çıkmaz.</li>
                           </>
                         ) : (
                           <>
                             <li className="flex gap-2"><span className="text-emerald-400">•</span> Excellent low latency (ping) time and synchronization.</li>
                             <li className="flex gap-2"><span className="text-emerald-400">•</span> Opportunity for fast and fluid card moves without getting stuck in traffic.</li>
                             <li className="flex gap-2"><span className="text-emerald-400">•</span> Sent content never leaves the limits of the encrypted channel.</li>
                           </>
                         )}
                       </ul>
                     </div>
                     <div className="bg-white/5 border border-white/5 rounded-2xl p-4 space-y-2">
                       <h4 className="text-white font-bold uppercase tracking-widest text-xs flex items-center gap-2">
                         <Info className="w-4 h-4 text-amber-400" /> 
                         {language === 'tr' ? 'Altyapı Notları' : 'Infrastructure Notes'}
                       </h4>
                       <ul className="space-y-2 text-white/50 text-xs">
                         {language === 'tr' ? (
                           <>
                             <li className="flex gap-2"><span className="text-amber-400">•</span> Modern Web özelliklerini destekleyen bir tarayıcı önerilir.</li>
                             <li className="flex gap-2"><span className="text-amber-400">•</span> Kurum içi yüksek güvenlikli proxyler mimarinin akışını filtreleyebilir.</li>
                           </>
                         ) : (
                           <>
                             <li className="flex gap-2"><span className="text-amber-400">•</span> A browser that supports modern Web features is recommended.</li>
                             <li className="flex gap-2"><span className="text-amber-400">•</span> Intranet high-security proxies may filter the architecture flow.</li>
                           </>
                         )}
                       </ul>
                     </div>
                  </div>

                  <p className="text-xs text-white/40 text-center uppercase tracking-widest mt-4 flex items-center justify-center gap-2">
                    <CheckCircle className="w-4 h-4 text-emerald-400" /> 
                    {language === 'tr' ? 'Oyuna arkadaşlarınız ID yazarak hemen katılabilir!' : 'Your friends can join the game instantly by typing the ID!'}
                  </p>
                </div>
                
                <div className="p-4 md:p-6 border-t border-white/5 flex flex-col sm:flex-row justify-end gap-3 shrink-0 bg-[#020617]/50">
                  <button
                     onClick={() => setShowP2PModal(false)}
                     className="flex items-center justify-center gap-2 px-8 py-3 w-full sm:w-auto bg-white/5 border border-white/10 hover:bg-white/10 text-white font-black uppercase tracking-widest text-sm rounded-xl transition-all"
                  >
                     {language === 'tr' ? 'Geri Dön' : 'Go Back'}
                  </button>
                </div>
             </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* About Modal */}
      <AnimatePresence>
        {showAboutModal && (
          <div className="fixed inset-0 z-[400] flex items-center justify-center p-4">
             <motion.div 
               initial={{ opacity: 0 }}
               animate={{ opacity: 1 }}
               exit={{ opacity: 0 }}
               onClick={() => setShowAboutModal(false)}
               className="absolute inset-0 bg-[#020617]/95 backdrop-blur-xl"
             />
             <motion.div 
               initial={{ scale: 0.95, opacity: 0, y: 30 }}
               animate={{ scale: 1, opacity: 1, y: 0 }}
               exit={{ scale: 0.95, opacity: 0, y: 30 }}
               transition={{ type: "spring", stiffness: 300, damping: 30 }}
               className="relative w-full max-w-2xl bg-slate-900/80 border border-white/10 rounded-[2.5rem] overflow-hidden shadow-[0_0_80px_rgba(79,70,229,0.15)] backdrop-blur-2xl flex flex-col"
             >
                <div className="flex items-center justify-between p-6 md:p-8 border-b border-white/10 bg-white/5">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-indigo-500/10 border border-indigo-500/30 flex items-center justify-center shadow-inner pt-1 pl-0.5">
                      <Info className="w-6 h-6 text-indigo-400" />
                    </div>
                    <div>
                      <h2 className="text-2xl md:text-3xl font-black italic tracking-tighter uppercase text-white">{language === 'tr' ? 'Hakkımızda' : 'About Us'}</h2>
                    </div>
                  </div>
                  <button 
                    onClick={() => setShowAboutModal(false)}
                    aria-label={language === 'tr' ? 'Kapat' : 'Close'}
                    className="p-3 bg-white/5 hover:bg-white/10 rounded-xl text-white/40 hover:text-white transition-all border border-white/5"
                  >
                    <X className="w-6 h-6" />
                  </button>
                </div>
                
                <div className="p-6 md:p-8 space-y-6 text-white/80 leading-relaxed font-medium">
                  {language === 'tr' ? (
                    <>
                      <p>
                        Pişti Online, geleneksel masa oyunlarını modern ve güvenli bir altyapıyla bir araya getiren yenilikçi bir platformdur. 
                        Hiçbir kurulum veya eklenti beklemeden, sadece tarayıcınız üzerinden doğrudan oyun oynamanın keyfini çıkarabilirsiniz.
                      </p>
                      <div className="p-4 bg-indigo-500/10 border border-indigo-500/20 rounded-xl space-y-2">
                        <h4 className="text-indigo-400 font-bold tracking-widest text-xs uppercase">Teknoloji & Güvenlik</h4>
                        <p className="text-sm text-indigo-200/80">
                          Meydan okumalarınız modern <strong>Eşler Arası (P2P)</strong> teknolojisi ve uçtan uca şifreleme ile izole bir şekilde gerçekleşir. Sunucu kaynaklı yavaşlamalar olmadan, arkadaşlarınızla güvenle rekabet edebilirsiniz.
                        </p>
                      </div>
                      <p>
                        Topluluğumuzdan gelen geri bildirimler bizim için çok değerlidir. Platformumuz sürekli olarak geliştirilmekte olup, oyun deneyiminizi iyileştirmek ve olası kural farklılıkları ya da hataları anında çözmek için çalışmalarımız aralıksız devam etmektedir. Bize katıldığınız için teşekkür ederiz!
                      </p>
                    </>
                  ) : (
                    <>
                      <p>
                        Pisti Online is an innovative platform that brings together traditional board and card games with a modern and secure infrastructure.
                        Enjoy gaming directly through your browser without any installation or plugin requirements.
                      </p>
                      <div className="p-4 bg-indigo-500/10 border border-indigo-500/20 rounded-xl space-y-2">
                        <h4 className="text-indigo-400 font-bold tracking-widest text-xs uppercase">Technology & Security</h4>
                        <p className="text-sm text-indigo-200/80">
                          Your challenges are hosted in an isolated manner using modern <strong>Peer-to-Peer (P2P)</strong> technology and end-to-end encryption. Compete securely with friends without server-side lag.
                        </p>
                      </div>
                      <p>
                        Feedback from our community is invaluable. Our platform is constantly being improved, and our efforts continue non-stop to enhance your gaming experience and resolve potential rule differences or bugs instantly. Thank you for joining us!
                      </p>
                    </>
                  )}
                </div>
                
                <div className="p-6 md:p-8 border-t border-white/5 flex justify-end bg-[#020617]/50">
                  <button
                     onClick={() => setShowAboutModal(false)}
                     className="px-8 py-3 w-full sm:w-auto bg-white/5 border border-white/10 hover:bg-white/10 text-white font-black uppercase tracking-widest text-sm rounded-xl transition-all"
                  >
                     {t('close')}
                  </button>
                </div>
             </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Contact & Support Modal */}
      <AnimatePresence>
        {showContactModal && (
          <div className="fixed inset-0 z-[400] flex items-center justify-center p-4">
             <motion.div 
               initial={{ opacity: 0 }}
               animate={{ opacity: 1 }}
               exit={{ opacity: 0 }}
               onClick={() => setShowContactModal(false)}
               className="absolute inset-0 bg-[#020617]/95 backdrop-blur-xl"
             />
             <motion.div 
               initial={{ scale: 0.95, opacity: 0, y: 30 }}
               animate={{ scale: 1, opacity: 1, y: 0 }}
               exit={{ scale: 0.95, opacity: 0, y: 30 }}
               transition={{ type: "spring", stiffness: 300, damping: 30 }}
               className="relative w-full max-w-xl bg-slate-900/90 border border-white/10 rounded-[2rem] overflow-hidden shadow-[0_0_80px_rgba(6,182,212,0.15)] backdrop-blur-2xl flex flex-col max-h-[90vh]"
             >
                <div className="flex items-center justify-between p-6 border-b border-white/10 bg-white/5">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-cyan-500/10 border border-cyan-500/30 flex items-center justify-center shadow-inner">
                      <Mail className="w-5 h-5 text-cyan-400" />
                    </div>
                    <div>
                      <h2 className="text-xl md:text-2xl font-black italic tracking-tighter uppercase text-white">
                        {language === 'tr' ? 'İletişim & Destek' : 'Contact & Support'}
                      </h2>
                      <p className="text-xs text-white/40">7/24 {language === 'tr' ? 'Geri Bildirim ve İletişim Portalı' : 'Feedback & Contact Portal'}</p>
                    </div>
                  </div>
                  <button 
                    onClick={() => setShowContactModal(false)}
                    aria-label={language === 'tr' ? 'Kapat' : 'Close'}
                    className="p-2.5 bg-white/5 hover:bg-white/10 rounded-xl text-white/40 hover:text-white transition-all border border-white/5"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
                
                <div className="p-6 overflow-y-auto space-y-5 text-white/80 leading-relaxed font-medium">
                  {/* Official Email Copy Card */}
                  <div className="bg-slate-950/70 rounded-2xl p-4 border border-white/10 space-y-3">
                    <div className="flex items-center justify-between gap-2 flex-wrap sm:flex-nowrap">
                      <div className="flex items-center gap-2.5 min-w-0">
                        <Mail className="w-4 h-4 text-cyan-400 shrink-0" />
                        <span className="text-sm font-mono text-cyan-300 font-bold underline underline-offset-2 truncate">mail@pisti.online</span>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <a 
                          href="mailto:mail@pisti.online"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="px-3 py-1.5 text-xs font-bold rounded-lg bg-cyan-500/20 text-cyan-300 hover:bg-cyan-500/30 transition-all uppercase tracking-wider flex items-center gap-1"
                        >
                          <ExternalLink className="w-3.5 h-3.5" />
                          <span>{language === 'tr' ? 'Mail Uygulaması' : 'Open App'}</span>
                        </a>
                        <button 
                          onClick={copyEmailToClipboard}
                          className={cn(
                            "px-3 py-1.5 text-xs font-bold rounded-lg transition-all uppercase tracking-wider",
                            contactCopied ? "bg-emerald-400 text-slate-950 font-black" : "bg-white/10 text-white/80 hover:bg-white/20"
                          )}
                        >
                          {contactCopied ? (language === 'tr' ? 'Kopyalandı' : 'Copied') : (language === 'tr' ? 'Kopyala' : 'Copy')}
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Contact Form */}
                  <div className="bg-white/5 border border-white/5 rounded-2xl p-4 space-y-3">
                    <h3 className="text-xs font-black text-cyan-400 uppercase tracking-widest flex items-center gap-2">
                      <MessageSquare className="w-4 h-4" />
                      {language === 'tr' ? 'Doğrudan Mesaj Gönder' : 'Send Direct Message'}
                    </h3>

                    {contactStatus === 'success' ? (
                      <div className="bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 p-4 rounded-xl text-xs space-y-2 text-center animate-in fade-in zoom-in-95">
                        <p className="font-extrabold text-sm">{language === 'tr' ? 'Mesajınız Başarıyla İletildi!' : 'Your Message Has Been Delivered!'}</p>
                        <p className="text-white/60 text-xs">
                          {language === 'tr' 
                            ? 'Mesajınız e-posta uygulamanıza yönlendirildi.' 
                            : 'Your message has been redirected to your email client.'}
                        </p>
                        <button 
                          onClick={() => setContactStatus('idle')}
                          className="mt-2 px-4 py-1.5 bg-emerald-400 text-slate-950 text-xs font-black uppercase tracking-wider rounded-lg hover:scale-105 active:scale-95 transition-transform"
                        >
                          {language === 'tr' ? 'Yeni Mesaj Gönder' : 'Send New Message'}
                        </button>
                      </div>
                    ) : (
                      <form 
                        onSubmit={(e) => {
                          e.preventDefault();
                          if (!contactMessage.trim() || !contactEmail.trim()) return;
                          
                          soundManager.play('click');
                          
                          const subject = encodeURIComponent(contactType ? `[Pişti Online - ${contactType}]` : '[Pişti Online İletişim]');
                          const body = encodeURIComponent(`Mesaj:\n${contactMessage}\n\n---\nGönderen E-posta: ${contactEmail}\nKullanıcı Adı: ${props.userName || 'Kullanıcı'}`);
                          
                          window.location.href = `mailto:mail@pisti.online?subject=${subject}&body=${body}`;
                          
                          setContactMessage('');
                        }}
                        className="space-y-3 text-xs"
                      >
                        <div>
                          <label className="block text-[10px] font-black text-white/40 uppercase tracking-widest mb-1">{language === 'tr' ? 'E-Posta Adresiniz' : 'Your Email Address'}</label>
                          <input 
                            type="email" 
                            required 
                            placeholder="ornek@eposta.com" 
                            value={contactEmail}
                            onChange={(e) => setContactEmail(e.target.value)}
                            className="w-full bg-slate-950/70 border border-white/10 rounded-xl px-3 py-2 text-white placeholder-white/20 focus:outline-none focus:border-cyan-400 transition-colors"
                          />
                        </div>

                        <div>
                          <label className="block text-[10px] font-black text-white/40 uppercase tracking-widest mb-1">{language === 'tr' ? 'Konu Kategorisi' : 'Subject Category'}</label>
                          <select 
                            value={contactType} 
                            onChange={(e) => setContactType(e.target.value)}
                            className="w-full bg-slate-950/70 border border-white/10 rounded-xl px-2 py-2 text-white focus:outline-none focus:border-cyan-400 transition-colors"
                          >
                            <option value="Öneri" className="bg-slate-900 text-white">{language === 'tr' ? 'Soru / Geliştirme Önerisi' : 'Question / Suggestion'}</option>
                            <option value="Hata" className="bg-slate-900 text-white">{language === 'tr' ? 'Oyun İçi Hata Bildirimi' : 'In-game Bug Report'}</option>
                            <option value="Sponsorluk" className="bg-slate-900 text-white">{language === 'tr' ? 'Sponsorluk / İş Birliği' : 'Sponsorship / Collaboration'}</option>
                            <option value="Diğer" className="bg-slate-900 text-white">{language === 'tr' ? 'Diğer Sorunlar' : 'Other Issues'}</option>
                          </select>
                        </div>

                        <div>
                          <label className="block text-[10px] font-black text-white/40 uppercase tracking-widest mb-1">{language === 'tr' ? 'Mesajınız' : 'Your Message'}</label>
                          <textarea 
                            required 
                            rows={3}
                            placeholder={language === 'tr' ? 'Sorunuzu, önerinizi veya mesajınızı buraya yazabilirsiniz...' : 'Write your question, suggestion or message here...'} 
                            value={contactMessage}
                            onChange={(e) => setContactMessage(e.target.value)}
                            className="w-full bg-slate-950/70 border border-white/10 rounded-xl px-3 py-2 text-white placeholder-white/20 focus:outline-none focus:border-cyan-400 transition-colors resize-none"
                          />
                        </div>

                        <button 
                          type="submit" 
                          disabled={contactStatus === 'sending'}
                          className="w-full py-2.5 bg-gradient-to-r from-cyan-500 to-indigo-600 hover:from-cyan-400 hover:to-indigo-500 text-slate-950 hover:text-white font-black uppercase tracking-wider rounded-xl transition-all shadow-md active:scale-95 flex items-center justify-center gap-2"
                        >
                          {contactStatus === 'sending' ? (
                            <>
                              <span className="w-3.5 h-3.5 border-2 border-slate-950 border-t-transparent rounded-full animate-spin" />
                              <span>{language === 'tr' ? 'Gönderiliyor...' : 'Sending...'}</span>
                            </>
                          ) : (
                            <>
                              <Send className="w-3.5 h-3.5 shrink-0" />
                              <span>{language === 'tr' ? 'E-posta Uygulamasını Aç' : 'Open Email App'}</span>
                            </>
                          )}
                        </button>
                      </form>
                    )}
                  </div>
                </div>
                
                <div className="p-4 border-t border-white/5 flex justify-end bg-[#020617]/50">
                  <button
                     onClick={() => setShowContactModal(false)}
                     className="px-6 py-2 bg-white/5 border border-white/10 hover:bg-white/10 text-white font-black uppercase tracking-widest text-xs rounded-xl transition-all"
                  >
                     {t('close')}
                  </button>
                </div>
             </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Main Menu (SidebarMenu) */}
      {isMenuOpen && (
        <Suspense fallback={null}>
          <SidebarMenu
            playerName={props.userName}
            onNameChange={props.onNameChange}
            playerAvatar={props.userAvatar}
            onAvatarChange={props.onAvatarChange}
            gameType={props.gameType}
            onUpdateProfile={() => {}}
            onExit={props.onLogout}
            selectedSkinId={props.selectedSkinId}
            onSkinSelect={props.onSkinSelect}
            faceStyle={props.faceStyle}
            onFaceStyleSelect={props.onFaceStyleSelect}
            stats={props.stats}
            isOpen={isMenuOpen}
            onClose={() => setIsMenuOpen(false)}
            onOpenGame={(gameType) => {
              setIsMenuOpen(false);
              openLobbyWithGame(gameType);
            }}
            onOpenTerms={props.onOpenTermsOfUse}
            onOpenCookie={props.onOpenCookiePolicy}
          />
        </Suspense>
      )}

      {/* Lobby Modal */}
      <AnimatePresence>
        {showLobbyModal && (
          <div className="fixed inset-0 z-[300] overflow-y-auto overflow-x-hidden flex flex-col items-center justify-start p-2 sm:p-4 min-h-[100dvh] overscroll-contain">
             <motion.div 
               initial={{ opacity: 0 }}
               animate={{ opacity: 1 }}
               exit={{ opacity: 0 }}
               onClick={toggleModal}
               className="fixed inset-0 bg-[#020617]/95 backdrop-blur-xl -z-10"
             />
             <motion.div 
               initial={{ scale: 0.94, opacity: 0, y: 20 }}
               animate={{ scale: 1, opacity: 1, y: 0 }}
               exit={{ scale: 0.94, opacity: 0, y: 20 }}
               className="relative w-full max-w-[480px] my-2 sm:my-auto py-1 sm:py-4 shrink-0"
             >
                <Suspense fallback={
                  <div className="flex flex-col items-center justify-center gap-4 text-cyan-400 py-16">
                    <div className="w-8 h-8 border-4 border-cyan-400/20 border-t-cyan-400 rounded-full animate-spin" />
                    <span className="text-xs font-semibold tracking-wider uppercase opacity-85">Lobi Yükleniyor...</span>
                  </div>
                }>
                  <Lobby {...props} initialStep={lobbyInitialStep} onClose={toggleModal} />
                </Suspense>
             </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );

  if (isPiPActive && pipContainer) {
    return (
      <>
        <PiPActivePlaceholder
          onRestore={closePiP}
          title={language === 'tr' ? 'Mini Ekran Aktif' : 'Mini Screen Active'}
          subtitle={
            language === 'tr'
              ? 'Pisti.online mini pencerede çalışıyor. Buradan veya mini pencereden dilediğiniz gibi kontrol edebilirsiniz.'
              : 'Pisti.online is running in Picture-in-Picture window.'
          }
        />
        {createPortal(renderedContent, pipContainer)}
      </>
    );
  }

  return renderedContent;
};
